import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../lib/api';
import pushNotificationService from '../lib/pushNotifications';
import AdminInstallPrompt from '../components/InstallPrompt';
import OfflineIndicator from '../components/OfflineIndicator';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import RichTextEditor from '../components/ui/RichTextEditor';
import UnifiedBookingsTab from '../components/admin/UnifiedBookingsTab';
import EnhancedPaymentsTab from '../components/admin/EnhancedPaymentsTab';
import UnifiedToursTab from '../components/admin/UnifiedToursTab';
import ServiceProvidersTab from '../components/admin/ServiceProvidersTab';
import CustomizeTab from '../components/admin/CustomizeTab';
import SettingsTab from '../components/admin/SettingsTab';
import MessagingCenterTab from '../components/admin/MessagingCenterTab';
import UsersTab from '../components/admin/UsersTab';
import FooterTab from '../components/admin/FooterTab';
import WaiverTemplatesTab from '../components/admin/WaiverTemplatesTab';
import ReferralCampaignsTab from '../components/admin/ReferralCampaignsTab';
import AnalyticsDashboardTab from '../components/admin/AnalyticsDashboardTab';
import GiftCardsTab from '../components/admin/GiftCardsTab';
import SEOTab from '../components/admin/SEOTab';
import AbandonedCartsTab from '../components/admin/AbandonedCartsTab';
import GroupDiscountsTab from '../components/admin/GroupDiscountsTab';
import LanguageSettingsTab from '../components/admin/LanguageSettingsTab';
import CustomerReferralTab from '../components/admin/CustomerReferralTab';
import UGCCampaignsTab from '../components/admin/UGCCampaignsTab';
import WhatsAppSettingsTab from '../components/admin/WhatsAppSettingsTab';
// Lazy load NotificationsTab to avoid loading large base64 audio data on initial render
const NotificationsTab = lazy(() => import('../components/admin/NotificationsTab'));
import TourAvailabilityCalendar from '../components/admin/TourAvailabilityCalendar';
import TemplateManagerTab from '../components/admin/TemplateManagerTab';
import TourFormModal from '../components/admin/TourFormModal';
import { toast } from 'sonner';
import { formatPrice, formatShortDate, getStatusColor, getPaymentStatusColor } from '../lib/utils';
import { 
  Compass, LayoutDashboard, Map as MapIcon, Calendar, Users, DollarSign,
  Plus, Edit2, Trash2, Loader2, LogOut, CheckCircle, XCircle, Check,
  Image, Link as LinkIcon, Clock, MapPin, Star, X, Upload, Clipboard,
  Settings, Phone, Mail, Facebook, Instagram, Twitter, Youtube,
  TrendingUp, Flame, Eye, Zap, Bell, AlertCircle, Search,
  CreditCard, FileText, Send, Copy, ExternalLink, MessageSquare,
  FolderOpen, GripVertical, Palette, UserCog, Shield, UserPlus, Pencil, Tag, Type,
  Building2, Share2, BarChart3, Gift, Trophy, ShoppingCart, Percent, Languages, Camera, Heart, MessageCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to convert 24hr time to 12hr AM/PM format
const formatTime12hr = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Helper function to convert 12hr time back to 24hr format for storage
const formatTime24hr = (time12) => {
  if (!time12) return '';
  const match = time12.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return time12; // Return as-is if not matching
  let [, hours, minutes, period] = match;
  hours = parseInt(hours, 10);
  if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
  if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
  return `${hours.toString().padStart(2, '0')}:${minutes}`;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAdmin, logout, loading: authLoading } = useAuth();
  
  // Parse URL parameters for Quick Actions (3D Touch shortcuts)
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get('tab') || 'bookings';
  const initialFilter = urlParams.get('filter') || null;
  const initialView = urlParams.get('view') || null;
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [bookingsFilter, setBookingsFilter] = useState(initialFilter);
  const [bookingsViewMode, setBookingsViewMode] = useState(initialView);
  
  // State for navigation to Messaging Center with pre-selected customer and content
  const [messagingRecipient, setMessagingRecipient] = useState(null);
  const [messagingPrefilledContent, setMessagingPrefilledContent] = useState({ subject: '', body: '' });
  const [stats, setStats] = useState(null);
  const [tours, setTours] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Notification state
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  
  // Push notification state
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(true);
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const [showPushBanner, setShowPushBanner] = useState(() => {
    // Don't show if user already completed the "Add to Home Screen" flow
    return localStorage.getItem('adminAppInstalled') !== 'true';
  });
  
  // State to pass selected email to MessagingCenterTab when clicking notification
  const [selectedEmailFromNotification, setSelectedEmailFromNotification] = useState(null);
  
  // Tour editing states
  const [tourModalOpen, setTourModalOpen] = useState(false);
  const [editingTour, setEditingTour] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      // Not logged in - redirect to login with return URL
      navigate('/login', { state: { from: '/admin' } });
      return;
    }
    
    // Check admin role directly to avoid function reference issues
    if (user.role !== 'admin') {
      // Logged in but not admin - redirect to home
      navigate('/');
      return;
    }
    
    // Load data with error handling
    loadData().catch(err => {
      console.error('Initial data load failed:', err);
      setLoading(false);
    });
    
    // Auto-enable push notifications for admins (with delay to prevent blocking render)
    const pushTimeout = setTimeout(() => {
      if (typeof navigator !== 'undefined' && !navigator.webdriver) {
        autoEnablePushNotifications().catch(err => {
          console.log('Push notification setup skipped:', err.message);
        });
      }
    }, 3000);
    
    return () => clearTimeout(pushTimeout);
  }, [user, navigate, authLoading]);

  // Auto-enable push notifications for admins (no permission prompt - automatic)
  const autoEnablePushNotifications = async () => {
    try {
      // Skip in headless/automated testing environments
      if (navigator.webdriver || window.Cypress || window.__PLAYWRIGHT__) {
        console.log('Push notifications skipped in test environment');
        return;
      }
      
      // Skip if running in an iframe or embedded context
      if (window.self !== window.top) {
        console.log('Push notifications skipped in iframe context');
        return;
      }
      
      const support = pushNotificationService.checkSupport();
      if (!support.supported) {
        console.log('Push notifications not supported on this device');
        return;
      }
      
      // Register service worker
      await pushNotificationService.registerServiceWorker();
      
      // Check if already subscribed
      const isSubscribed = await pushNotificationService.isSubscribed();
      if (isSubscribed) {
        console.log('Already subscribed to push notifications');
        return;
      }
      
      // Check permission state
      const permission = pushNotificationService.getPermissionState();
      
      // Check if user already completed "Add to Home Screen" flow
      const alreadyInstalled = localStorage.getItem('adminAppInstalled') === 'true';
      
      if (permission === 'granted') {
        // Permission already granted, subscribe silently
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('tour_finder_token');
        if (token) {
          await pushNotificationService.subscribeToPush(apiUrl, token);
          console.log('Auto-subscribed to push notifications');
          setPushEnabled(true);
          setShowPushBanner(false);
        }
      } else if (permission === 'default') {
        // Skip showing banner if user already confirmed they added to home screen
        if (alreadyInstalled) {
          console.log('User already confirmed app installation, skipping push banner');
          setShowPushBanner(false);
          return;
        }
        
        // Permission not yet asked - show banner instead of auto-requesting
        // iOS is stricter about when you can request permissions
        const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
          || window.navigator.standalone === true;
        
        setIsIOSDevice(iOS);
        setIsPWA(isStandalone);
        
        if (iOS && !isStandalone) {
          // iOS needs PWA mode for push notifications
          setShowPushBanner(true);
        } else {
          // Show banner for other devices/browsers
          setShowPushBanner(true);
        }
      } else if (permission === 'denied') {
        // User blocked notifications - don't show banner
        setPushEnabled(false);
        setShowPushBanner(false);
      }
    } catch (error) {
      console.error('Failed to auto-enable push notifications:', error);
    }
  };

  // Handle dismissing push banner - remember user preference
  const handleDismissPushBanner = () => {
    setShowPushBanner(false);
    // Remember that user dismissed the banner so we don't show it again
    localStorage.setItem('adminAppInstalled', 'true');
  };

  // Handle enable push notifications (called from banner)
  const handleEnablePush = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('tour_finder_token');
      
      if (!token) {
        toast.error('Please log in again');
        return;
      }
      
      // Register service worker first
      await pushNotificationService.registerServiceWorker();
      
      // Request permission
      const permission = await pushNotificationService.requestPermission();
      
      if (permission === 'granted') {
        // Subscribe to push
        await pushNotificationService.subscribeToPush(apiUrl, token);
        setPushEnabled(true);
        setShowPushBanner(false);
        // Remember that user completed the setup
        localStorage.setItem('adminAppInstalled', 'true');
        toast.success('Push notifications enabled! You will now receive booking alerts on your lock screen.');
      } else if (permission === 'denied') {
        toast.error('Notification permission denied. Please enable notifications in your browser/phone settings.');
        setShowPushBanner(false);
        // Don't ask again after denial
        localStorage.setItem('adminAppInstalled', 'true');
      }
    } catch (error) {
      console.error('Failed to enable push notifications:', error);
      toast.error('Failed to enable notifications. Please try again.');
    }
  };

  const loadData = async () => {
    try {
      const [statsRes, toursRes, bookingsRes, settingsRes, categoriesRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getAllTours(),
        adminAPI.getAllBookings(),
        adminAPI.getSettings(),
        adminAPI.getAllCategories(),
      ]);
      setStats(statsRes.data);
      setTours(toursRes.data);
      setBookings(bookingsRes.data);
      setSiteSettings(settingsRes.data);
      setCategories(categoriesRes.data || []);
      
      // Load notification count
      loadNotifications();
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadNotifications = async () => {
    try {
      const [countRes, notificationsRes] = await Promise.all([
        adminAPI.getUnreadNotificationCount(),
        adminAPI.getNotifications(false)
      ]);
      const unreadCount = countRes.data?.unread_count || 0;
      setUnreadNotifications(unreadCount);
      setNotifications(notificationsRes.data || []);
      
      // Update app badge with unread count
      pushNotificationService.setBadge(unreadCount);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };
  
  const markNotificationRead = async (notificationId) => {
    try {
      await adminAPI.markNotificationRead(notificationId);
      loadNotifications();
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };
  
  const markAllNotificationsRead = async () => {
    try {
      await adminAPI.markAllNotificationsRead();
      loadNotifications();
      // Clear app badge when all notifications are read
      pushNotificationService.clearBadge();
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications read:', error);
    }
  };

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Send browser push notification
  const sendBrowserNotification = (title, body, icon = '🔔') => {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification(title, {
          body,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: 'tour-finder-notification',
          requireInteraction: true
        });
      } catch (e) {
        console.log('Browser notification failed:', e);
      }
    }
  };

  // Track previous notification count for detecting new ones
  const prevNotificationCount = useRef(notifications.length);
  const notificationsRef = useRef(notifications);
  
  // Keep ref in sync with state
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);
  
  // Poll for new notifications every 15 seconds (faster for real-time alerts)
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    
    const interval = setInterval(async () => {
      try {
        const res = await adminAPI.getNotifications();
        const newNotifs = res.data || [];
        
        // Check if there are NEW unread notifications
        const newUnread = newNotifs.filter(n => !n.read);
        const prevUnread = notificationsRef.current.filter(n => !n.read);
        
        if (newUnread.length > prevUnread.length) {
          const latestNotif = newNotifs[0];
          if (latestNotif && !latestNotif.read) {
            // Show toast
            toast.info(`🔔 ${latestNotif.title}`, { duration: 5000 });
            
            // Send browser notification if tab is not focused
            if (document.hidden) {
              sendBrowserNotification(
                latestNotif.title,
                latestNotif.message,
                latestNotif.type === 'new_booking' ? '📦' : '🔔'
              );
            }
          }
        }
        
        setNotifications(newNotifs);
        setUnreadNotifications(newUnread.length);
      } catch (error) {
        console.error('Error polling notifications:', error);
      }
    }, 15000); // 15 seconds for faster booking alerts
    
    return () => clearInterval(interval);
  }, [user]);

  // Track pending bookings count and new (unviewed) bookings count
  const [pendingBookingsCount, setPendingBookingsCount] = useState(0);
  const [newBookingsCount, setNewBookingsCount] = useState(0);
  const bookingsLengthRef = useRef(0);
  
  // Keep ref in sync
  useEffect(() => {
    bookingsLengthRef.current = bookings.length;
  }, [bookings.length]);

  // Auto-refresh all data every 15 seconds
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    
    const dataInterval = setInterval(async () => {
      try {
        // Silently refresh bookings data
        const res = await adminAPI.getAllBookings();
        const newBookings = res.data || [];
        
        // Count pending bookings (payment_status = pending)
        const pendingCount = newBookings.filter(b => b.payment_status === 'pending').length;
        setPendingBookingsCount(pendingCount);
        
        // Count new (unviewed) bookings
        const unviewedCount = newBookings.filter(b => b.is_viewed === false).length;
        setNewBookingsCount(unviewedCount);
        
        // Check for new bookings
        if (newBookings.length > bookingsLengthRef.current) {
          const newCount = newBookings.length - bookingsLengthRef.current;
          toast.info(`📦 ${newCount} new booking(s) arrived!`, { duration: 5000 });
          
          // Browser notification
          if (document.hidden) {
            sendBrowserNotification('New Booking!', `${newCount} new booking(s) received`);
          }
        }
        
        setBookings(newBookings);
      } catch (error) {
        console.error('Error refreshing bookings:', error);
      }
    }, 15000);
    
    return () => clearInterval(dataInterval);
  }, [user]);

  // Initial load of pending count and new bookings count
  useEffect(() => {
    if (bookings.length > 0) {
      const pendingCount = bookings.filter(b => b.payment_status === 'pending').length;
      setPendingBookingsCount(pendingCount);
      
      const unviewedCount = bookings.filter(b => b.is_viewed === false).length;
      setNewBookingsCount(unviewedCount);
    }
  }, [bookings]);

  const handleDeleteTour = async (tourId) => {
    if (!window.confirm('Are you sure you want to delete this tour?')) return;
    
    try {
      await adminAPI.deleteTour(tourId);
      toast.success('Tour deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete tour');
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await adminAPI.updateBookingStatus(bookingId, status);
      toast.success('Booking status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update booking status');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 flex" data-testid="admin-dashboard">
      {/* Offline Indicator */}
      <OfflineIndicator />
      
      {/* Sidebar */}
      <aside className="w-64 bg-foreground text-white flex flex-col h-screen sticky top-0" data-testid="admin-sidebar">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={siteSettings?.site_logo_url} 
              alt="Tour Finder" 
              className="h-8 w-8 object-contain"
            />
            <span className="font-heading text-xl font-bold">TOUR FINDER</span>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <button
            onClick={() => setActiveTab('notifications')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative ${
              activeTab === 'notifications' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-notifications"
          >
            <Bell className="h-5 w-5" />
            Notifications
            {unreadNotifications > 0 && (
              <span className="absolute right-4 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'bookings' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-bookings"
          >
            <Calendar className="h-5 w-5" />
            <span className="flex-1 text-left">Bookings</span>
            {newBookingsCount > 0 && (
              <Badge className="bg-blue-500 text-white text-xs px-2 py-0.5 animate-pulse" data-testid="new-bookings-badge">
                {newBookingsCount} new
              </Badge>
            )}
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'payments' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-payments"
          >
            <CreditCard className="h-5 w-5" />
            Payments
          </button>
          <button
            onClick={() => setActiveTab('hotels')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'hotels' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-hotels"
          >
            <MapPin className="h-5 w-5" />
            Pickup Locations
          </button>
          <button
            onClick={() => setActiveTab('tours')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'tours' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-tours"
          >
            <MapIcon className="h-5 w-5" />
            Tours & Categories
          </button>
          <button
            onClick={() => setActiveTab('customize')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'customize' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-customize"
          >
            <Palette className="h-5 w-5" />
            Customize
          </button>
          <button
            onClick={() => setActiveTab('footer')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'footer' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-footer"
          >
            <FileText className="h-5 w-5" />
            Edit Footer
          </button>
          <button
            onClick={() => setActiveTab('providers')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'providers' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-providers"
          >
            <Building2 className="h-5 w-5" />
            Service Providers
          </button>
          <button
            onClick={() => setActiveTab('waiver')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'waiver' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-waiver"
          >
            <Clipboard className="h-5 w-5" />
            Waiver Management
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'users' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-users"
          >
            <UserCog className="h-5 w-5" />
            Manage Users
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'settings' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-settings"
          >
            <Settings className="h-5 w-5" />
            Settings
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'templates' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-templates"
          >
            <FileText className="h-5 w-5" />
            Templates
          </button>
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'whatsapp' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-whatsapp"
          >
            <MessageCircle className="h-5 w-5" />
            WhatsApp
          </button>
          
          {/* Enhancement Features */}
          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="px-4 text-xs text-gray-400 uppercase tracking-wider mb-2">Growth Tools</p>
          </div>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'analytics' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-analytics"
          >
            <BarChart3 className="h-5 w-5" />
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('referrals')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'referrals' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-referrals"
          >
            <Share2 className="h-5 w-5" />
            Referral Campaigns
          </button>
          <button
            onClick={() => setActiveTab('gift-cards')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'gift-cards' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-gift-cards"
          >
            <Gift className="h-5 w-5" />
            Gift Cards
          </button>
          <button
            onClick={() => setActiveTab('seo')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'seo' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-seo"
          >
            <Search className="h-5 w-5" />
            SEO & Marketing
          </button>
          <button
            onClick={() => setActiveTab('abandoned-carts')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'abandoned-carts' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-abandoned-carts"
          >
            <ShoppingCart className="h-5 w-5" />
            Abandoned Carts
          </button>
          <button
            onClick={() => setActiveTab('group-discounts')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'group-discounts' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-group-discounts"
          >
            <Percent className="h-5 w-5" />
            Group Discounts
          </button>
          <button
            onClick={() => setActiveTab('customer-referrals')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'customer-referrals' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-customer-referrals"
          >
            <Heart className="h-5 w-5" />
            Customer Referrals
          </button>
          <button
            onClick={() => setActiveTab('ugc-campaigns')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'ugc-campaigns' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-ugc-campaigns"
          >
            <Camera className="h-5 w-5" />
            UGC Campaigns
          </button>
          <button
            onClick={() => setActiveTab('language-settings')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'language-settings' ? 'bg-primary text-white' : 'hover:bg-white/10'
            }`}
            data-testid="nav-language-settings"
          >
            <Languages className="h-5 w-5" />
            Multi-Language
          </button>
        </nav>

        <div className="p-4 border-t border-white/10">
          {/* Notification Bell */}
          <div className="relative mb-4">
            <Button
              variant="ghost"
              className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10 relative"
              onClick={() => setShowNotifications(!showNotifications)}
              data-testid="notification-bell"
            >
              <Bell className="h-5 w-5 mr-2" />
              Notifications
              {unreadNotifications > 0 && (
                <span className="absolute top-1 left-6 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadNotifications > 9 ? '9+' : unreadNotifications}
                </span>
              )}
            </Button>
            
            {/* Notification Dropdown */}
            {showNotifications && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-xl border max-h-80 overflow-y-auto z-50">
                <div className="p-3 border-b bg-gray-50 flex items-center justify-between sticky top-0">
                  <span className="font-semibold text-gray-800 text-sm">Notifications</span>
                  {unreadNotifications > 0 && (
                    <button
                      onClick={markAllNotificationsRead}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No notifications
                  </div>
                ) : (
                  notifications.slice(0, 10).map((notif) => (
                    <div
                      key={notif.id}
                      className={`p-3 border-b hover:bg-gray-50 ${!notif.read ? 'bg-blue-50' : ''}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${!notif.read ? 'bg-blue-500' : 'bg-gray-300'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800">{notif.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(notif.created_at).toLocaleString()}
                          </p>
                          
                          {/* Quick Actions for Booking Confirmations */}
                          {(notif.type === 'new_booking' || notif.type === 'payment_received' || notif.data?.booking_id) && notif.data && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {notif.data.customer_email && (
                                <a
                                  href={`mailto:${notif.data.customer_email}?subject=${encodeURIComponent(`🎉 Booking Confirmed - #${notif.data.reference || ''}`)}&body=${encodeURIComponent(`Hi ${notif.data.customer_name || 'there'},\n\nYour booking has been confirmed!\n\nTour: ${notif.data.tour_title || ''}\nDate: ${notif.data.date || ''}\nTime: ${notif.data.time || ''}\n\nWe look forward to seeing you!\n\n- Tour Finder Team\ntourfinder.puertorico@gmail.com\n+1 (787) 929-7723`)}`}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markNotificationRead(notif.id);
                                  }}
                                >
                                  <Mail className="h-3 w-3" />
                                  Send Email
                                </a>
                              )}
                              {notif.data.customer_phone && (
                                <a
                                  href={`sms:${notif.data.customer_phone}?body=${encodeURIComponent(`🎉 BOOKING CONFIRMED!\n\n#${notif.data.reference || ''} - ${notif.data.tour_title || ''}\n📅 ${notif.data.date || ''} at ${notif.data.time || ''}\n\nQuestions? Call +1(787)929-7723\n- Tour Finder PR`)}`}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded hover:bg-green-200"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markNotificationRead(notif.id);
                                  }}
                                >
                                  <MessageSquare className="h-3 w-3" />
                                  Send SMS
                                </a>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveTab('bookings');
                                  setShowNotifications(false);
                                  markNotificationRead(notif.id);
                                }}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200"
                              >
                                View Booking
                              </button>
                            </div>
                          )}
                          
                          {/* Quick Actions for Waiver Notifications */}
                          {(notif.type === 'waiver_signed' || notif.type === 'waivers_complete') && (
                            <div className="flex gap-2 mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveTab('bookings');
                                  setShowNotifications(false);
                                  markNotificationRead(notif.id);
                                }}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded hover:bg-green-200"
                              >
                                <CheckCircle className="h-3 w-3" />
                                View Booking
                              </button>
                            </div>
                          )}
                          
                          {/* Email notification click handler */}
                          {notif.type === 'new_email' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                let emailAddress = notif.data?.email_address || notif.data?.sender_email || notif.data?.from_email;
                                if (!emailAddress && notif.title) {
                                  const titleMatch = notif.title.match(/from\s+([^\s]+@[^\s]+)/i);
                                  if (titleMatch) emailAddress = titleMatch[1];
                                }
                                if (emailAddress) setSelectedEmailFromNotification(emailAddress);
                                setActiveTab('messaging');
                                setShowNotifications(false);
                                markNotificationRead(notif.id);
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 mt-2"
                            >
                              <Mail className="h-3 w-3" />
                              Reply to Email
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3 mb-4 px-4">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium text-sm">{user?.name}</p>
              <p className="text-xs text-gray-400">{user?.email}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Push Notification Banner */}
        {showPushBanner && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3">
            <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-full">
                  <Bell className="h-5 w-5" />
                </div>
                <div>
                  {isIOSDevice && !isPWA ? (
                    <>
                      <p className="font-semibold">Install App for Notifications</p>
                      <p className="text-sm opacity-90">
                        Tap <span className="inline-flex items-center px-1.5 py-0.5 bg-white/20 rounded text-xs">
                          Share
                        </span> then "Add to Home Screen" to receive booking alerts
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold">Enable Push Notifications</p>
                      <p className="text-sm opacity-90">
                        Get instant alerts when new bookings come in, even when your phone is locked!
                      </p>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(!isIOSDevice || isPWA) && (
                  <Button 
                    onClick={handleEnablePush}
                    className="bg-white text-blue-600 hover:bg-blue-50 font-semibold"
                    size="sm"
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    Enable Now
                  </Button>
                )}
                <Button 
                  onClick={handleDismissPushBanner}
                  variant="ghost" 
                  size="sm"
                  className="text-white hover:bg-white/20"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
        
        <div className="p-8">
          {activeTab === 'notifications' && (
            <Suspense fallback={<div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
              <NotificationsTab 
                notifications={notifications}
                unreadCount={unreadNotifications}
                onMarkRead={markNotificationRead}
                onMarkAllRead={markAllNotificationsRead}
                onRefresh={loadNotifications}
                onNavigateToEmail={(emailAddress) => {
                  setSelectedEmailFromNotification(emailAddress);
                  setActiveTab('messaging');
                }}
                onNavigateToBooking={(bookingId) => {
                  // Future: could expand booking in UnifiedBookingsTab
                  setActiveTab('bookings');
                }}
                onBulkEmail={async (recipients, subject, body) => {
                  // Send bulk emails
                  for (const recipient of recipients) {
                    try {
                      await adminAPI.sendCustomerEmail({
                        to_email: recipient.email,
                      subject: subject,
                      body: body,
                      customer_name: recipient.name
                    });
                  } catch (error) {
                    console.error(`Failed to send to ${recipient.email}:`, error);
                  }
                }
                toast.success(`Sent ${recipients.length} emails successfully!`);
              }}
            />
            </Suspense>
          )}
          {activeTab === 'bookings' && (
            <UnifiedBookingsTab 
              bookings={bookings}
              tours={tours}
              onUpdateStatus={handleUpdateBookingStatus}
              onRefresh={loadData}
              onNavigateToMessaging={(customerEmail, customerName) => {
                setMessagingRecipient({ email: customerEmail, name: customerName });
                setActiveTab('messaging');
              }}
              initialFilter={bookingsFilter}
              initialViewMode={bookingsViewMode}
            />
          )}
          {activeTab === 'payments' && (
            <EnhancedPaymentsTab onRefresh={loadData} />
          )}
          {activeTab === 'hotels' && (
            <PickupHotelsTab onRefresh={loadData} />
          )}
          {activeTab === 'tours' && (
            <UnifiedToursTab 
              tours={tours}
              categories={categories}
              onRefresh={loadData}
              onEditTour={(tour) => {
                setEditingTour(tour);
                setTourModalOpen(true);
              }}
              onDeleteTour={handleDeleteTour}
            />
          )}
          {activeTab === 'customize' && (
            <CustomizeTab 
              tours={tours} 
              onRefresh={loadData}
            />
          )}
          {activeTab === 'footer' && (
            <FooterTab settings={siteSettings} onRefresh={loadData} />
          )}
          {activeTab === 'providers' && (
            <ServiceProvidersTab tours={tours} bookings={bookings} onRefresh={loadData} />
          )}
          {activeTab === 'waiver' && (
            <WaiverTemplatesTab />
          )}
          {activeTab === 'referrals' && (
            <ReferralCampaignsTab 
              onNavigateToMessaging={(email, name, subject, body) => {
                setMessagingRecipient({ email, name });
                setMessagingPrefilledContent({ subject: subject || '', body: body || '' });
                setActiveTab('messaging');
              }}
            />
          )}
          {activeTab === 'users' && (
            <UsersTab onRefresh={loadData} currentUser={user} />
          )}
          {activeTab === 'settings' && (
            <SettingsTab settings={siteSettings} onRefresh={loadData} />
          )}
          {activeTab === 'templates' && (
            <TemplateManagerTab />
          )}
          {activeTab === 'whatsapp' && (
            <WhatsAppSettingsTab />
          )}
          {activeTab === 'analytics' && (
            <AnalyticsDashboardTab />
          )}
          {activeTab === 'gift-cards' && (
            <GiftCardsTab 
              onNavigateToMessaging={(email, name, subject, body) => {
                setMessagingRecipient({ email, name });
                setMessagingPrefilledContent({ subject: subject || '', body: body || '' });
                setActiveTab('messaging');
              }}
            />
          )}
          {activeTab === 'seo' && (
            <SEOTab />
          )}
          {activeTab === 'abandoned-carts' && (
            <AbandonedCartsTab />
          )}
          {activeTab === 'group-discounts' && (
            <GroupDiscountsTab />
          )}
          {activeTab === 'customer-referrals' && (
            <CustomerReferralTab />
          )}
          {activeTab === 'ugc-campaigns' && (
            <UGCCampaignsTab />
          )}
          {activeTab === 'language-settings' && (
            <LanguageSettingsTab />
          )}
        </div>
      </main>
      
      {/* Tour Form Modal - rendered at parent level for UnifiedToursTab */}
      <TourFormModal 
        open={tourModalOpen}
        onClose={() => setTourModalOpen(false)}
        tour={editingTour}
        onSuccess={() => {
          setTourModalOpen(false);
          setEditingTour(null);
          loadData();
        }}
      />
      
      {/* Admin Install Prompt - shows only for admins */}
      <AdminInstallPrompt />
    </div>
  );
}

// Dashboard Tab Component
function DashboardTab({ stats, bookings }) {
  const [activeSection, setActiveSection] = useState('overview');
  const [sendingSms, setSendingSms] = useState({});
  
  // Calculate today and tomorrow dates
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dayAfterTomorrow = new Date(tomorrow);
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);

  // Filter bookings
  const todayBookings = (bookings || []).filter(b => {
    const bookingDate = new Date(b.date || b.tour_date);
    bookingDate.setHours(0, 0, 0, 0);
    return bookingDate.getTime() === today.getTime() && b.status !== 'cancelled';
  });

  const tomorrowBookings = (bookings || []).filter(b => {
    const bookingDate = new Date(b.date || b.tour_date);
    bookingDate.setHours(0, 0, 0, 0);
    return bookingDate.getTime() === tomorrow.getTime() && b.status !== 'cancelled';
  });
  
  // New bookings (last 7 days)
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const newBookings = (bookings || []).filter(b => {
    const createdAt = new Date(b.created_at);
    return createdAt >= weekAgo;
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  
  // Booking history (all past bookings)
  const pastBookings = (bookings || []).filter(b => {
    const bookingDate = new Date(b.date || b.tour_date);
    bookingDate.setHours(0, 0, 0, 0);
    return bookingDate < today;
  }).sort((a, b) => new Date(b.date || b.tour_date) - new Date(a.date || a.tour_date));

  const pendingPayments = (bookings || []).filter(b => b.payment_status === 'pending');
  const todayRevenue = todayBookings.filter(b => b.payment_status === 'paid').reduce((sum, b) => sum + (b.total_price || 0), 0);

  // Format time helper
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };
  
  // Quick actions
  const handleCall = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };
  
  const handleSms = async (booking) => {
    const key = booking.id;
    setSendingSms(prev => ({ ...prev, [key]: true }));
    try {
      // Open SMS with pre-filled message
      const phone = booking.booker_phone || '';
      const message = encodeURIComponent(`Hi ${booking.booker_name || 'there'}! This is Tour Finder PR. Your tour "${booking.tour_title}" is scheduled for ${booking.date} at ${formatTime(booking.time)}. See you soon!`);
      window.location.href = `sms:${phone}?body=${message}`;
    } finally {
      setTimeout(() => setSendingSms(prev => ({ ...prev, [key]: false })), 1000);
    }
  };
  
  const handleWhatsApp = (booking) => {
    const phone = (booking.booker_phone || '').replace(/\D/g, '');
    const message = encodeURIComponent(`Hi ${booking.booker_name || 'there'}! This is Tour Finder PR. Your tour "${booking.tour_title}" is scheduled for ${booking.date} at ${formatTime(booking.time)}. See you soon!`);
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };
  
  // Booking card component with quick actions
  const BookingCard = ({ booking, variant = 'default' }) => {
    const bgClass = variant === 'today' ? 'bg-red-50/50 border-red-100' : 
                   variant === 'tomorrow' ? 'bg-amber-50/50 border-amber-100' : 
                   'bg-white border-border';
    
    return (
      <div className={`p-4 border rounded-lg ${bgClass}`}>
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-bold text-lg truncate">{booking.booker_name || booking.user_email?.split('@')[0]}</p>
              {booking.reference_number && (
                <Badge variant="outline" className="text-xs font-mono">#{booking.reference_number}</Badge>
              )}
            </div>
            <p className="font-semibold text-primary truncate">{booking.tour_title}</p>
          </div>
          <Badge className={booking.payment_status === 'paid' ? 'bg-green-500' : 'bg-amber-500'}>
            {booking.payment_status}
          </Badge>
        </div>
        <div className="grid grid-cols-3 gap-2 text-sm mt-3">
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{formatTime(booking.time)}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{booking.guests} guests</span>
          </div>
          <div className="text-right font-bold text-green-600">
            {formatPrice(booking.total_price)}
          </div>
        </div>
        
        {/* Contact Info & Quick Actions */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
          <div className="flex items-center gap-3 text-sm">
            {booking.booker_phone && (
              <span className="flex items-center gap-1 text-muted-foreground">
                <Phone className="h-3 w-3" />
                {booking.booker_phone}
              </span>
            )}
            {booking.pickup_hotel && (
              <span className="flex items-center gap-1 text-muted-foreground truncate max-w-[120px]">
                <MapPin className="h-3 w-3" />
                {booking.pickup_hotel}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* Receipt Download - only for paid bookings */}
            {booking.payment_status === 'paid' && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 bg-blue-50 hover:bg-blue-100"
                onClick={async () => {
                  try {
                    const response = await adminAPI.downloadReceipt(booking.id);
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `receipt_${booking.reference_number || booking.id.slice(0,5)}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    toast.error('Failed to download receipt');
                  }
                }}
                title="Download Receipt"
                data-testid={`download-receipt-${booking.id}`}
              >
                <FileText className="h-4 w-4 text-blue-600" />
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8"
              onClick={() => handleCall(booking.booker_phone)}
              disabled={!booking.booker_phone}
              title="Call"
            >
              <Phone className="h-4 w-4 text-blue-600" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8"
              onClick={() => handleSms(booking)}
              disabled={!booking.booker_phone}
              title="SMS"
            >
              <MessageSquare className="h-4 w-4 text-purple-600" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 bg-green-50 hover:bg-green-100"
              onClick={() => handleWhatsApp(booking)}
              disabled={!booking.booker_phone}
              title="WhatsApp"
            >
              <MessageSquare className="h-4 w-4 text-green-600" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="dashboard-tab">
      <h1 className="font-heading text-3xl font-bold mb-2">Dashboard</h1>
      <p className="text-muted-foreground mb-6">
        {today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
      </p>
      
      {/* Quick Navigation Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: 'overview', label: 'Overview', icon: LayoutDashboard },
          { id: 'today', label: `Today (${todayBookings.length})`, icon: Bell },
          { id: 'tomorrow', label: `Tomorrow (${tomorrowBookings.length})`, icon: Clock },
          { id: 'new', label: `New (${newBookings.length})`, icon: Zap },
          { id: 'history', label: 'History', icon: Calendar },
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeSection === tab.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveSection(tab.id)}
            className="gap-2"
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Overview Section */}
      {activeSection === 'overview' && (
        <>
          {/* Priority Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card 
              className="p-5 rounded-xl bg-red-50 border-red-200 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveSection('today')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-700">Today's Tours</p>
                  <p className="text-4xl font-bold text-red-600">{todayBookings.length}</p>
                  <p className="text-xs text-red-600 mt-1">{formatPrice(todayRevenue)} revenue</p>
                </div>
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center">
                  <Bell className="h-7 w-7 text-red-600" />
                </div>
              </div>
            </Card>

            <Card 
              className="p-5 rounded-xl bg-amber-50 border-amber-200 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveSection('tomorrow')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-amber-700">Tomorrow's Tours</p>
                  <p className="text-4xl font-bold text-amber-600">{tomorrowBookings.length}</p>
                  <p className="text-xs text-amber-600 mt-1">Upcoming</p>
                </div>
                <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="h-7 w-7 text-amber-600" />
                </div>
              </div>
            </Card>

            <Card 
              className="p-5 rounded-xl cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveSection('new')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">New Bookings</p>
                  <p className="text-4xl font-bold text-foreground">{newBookings.length}</p>
                  <p className="text-xs text-muted-foreground mt-1">Last 7 days</p>
                </div>
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Zap className="h-7 w-7 text-blue-600" />
                </div>
              </div>
            </Card>

            <Card className="p-5 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Revenue</p>
                  <p className="text-3xl font-bold text-foreground">{formatPrice(stats?.total_revenue || 0)}</p>
                  <p className="text-xs text-muted-foreground mt-1">All time</p>
                </div>
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="h-7 w-7 text-green-600" />
                </div>
              </div>
            </Card>
          </div>

          {/* Today's & Tomorrow's Bookings Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                  <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                  Today's Bookings
                </h2>
                <Badge variant="destructive">{todayBookings.length} tours</Badge>
              </div>
              
              {todayBookings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No tours scheduled for today</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {todayBookings.map((booking) => (
                    <BookingCard key={booking.id} booking={booking} variant="today" />
                  ))}
                </div>
              )}
            </Card>

            <Card className="rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                  <span className="w-3 h-3 bg-amber-500 rounded-full"></span>
                  Tomorrow's Bookings
                </h2>
                <Badge variant="secondary">{tomorrowBookings.length} tours</Badge>
              </div>
              
              {tomorrowBookings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No tours scheduled for tomorrow</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {tomorrowBookings.map((booking) => (
                    <BookingCard key={booking.id} booking={booking} variant="tomorrow" />
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}

      {/* Today Section */}
      {activeSection === 'today' && (
        <Card className="rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2">
              <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
              Today's Tours
            </h2>
            <Badge variant="destructive">{todayBookings.length} tours</Badge>
          </div>
          
          {todayBookings.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No tours scheduled for today</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {todayBookings.map((booking) => (
                <BookingCard key={booking.id} booking={booking} variant="today" />
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Tomorrow Section */}
      {activeSection === 'tomorrow' && (
        <Card className="rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2">
              <span className="w-3 h-3 bg-amber-500 rounded-full"></span>
              Tomorrow's Tours
            </h2>
            <Badge variant="secondary">{tomorrowBookings.length} tours</Badge>
          </div>
          
          {tomorrowBookings.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No tours scheduled for tomorrow</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tomorrowBookings.map((booking) => (
                <BookingCard key={booking.id} booking={booking} variant="tomorrow" />
              ))}
            </div>
          )}
        </Card>
      )}

      {/* New Bookings Section */}
      {activeSection === 'new' && (
        <Card className="rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2">
              <Zap className="h-5 w-5 text-blue-500" />
              New Bookings (Last 7 Days)
            </h2>
            <Badge>{newBookings.length} new</Badge>
          </div>
          
          {newBookings.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Zap className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No new bookings this week</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {newBookings.map((booking) => (
                <BookingCard key={booking.id} booking={booking} />
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Booking History Section */}
      {activeSection === 'history' && (
        <Card className="rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Booking History
            </h2>
            <Badge variant="outline">{pastBookings.length} past tours</Badge>
          </div>
          
          {pastBookings.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No past bookings</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {pastBookings.slice(0, 50).map((booking) => (
                <div key={booking.id} className="p-4 border rounded-lg bg-muted/30">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold">{booking.booker_name || booking.user_email?.split('@')[0]}</p>
                      <p className="text-sm text-primary">{booking.tour_title}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(booking.date || booking.tour_date).toLocaleDateString('en-US', { 
                          month: 'short', day: 'numeric', year: 'numeric' 
                        })} at {formatTime(booking.time)}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className={booking.payment_status === 'paid' ? 'bg-green-500' : 'bg-amber-500'}>
                        {booking.payment_status}
                      </Badge>
                      <p className="text-sm font-bold mt-1">{formatPrice(booking.total_price)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// Tours Tab Component
function ToursTab({ tours, onDelete, onRefresh }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTour, setEditingTour] = useState(null);

  const handleEdit = (tour) => {
    setEditingTour(tour);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setEditingTour(null);
    setModalOpen(true);
  };

  return (
    <div data-testid="tours-tab">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold">Manage Tours</h1>
          <p className="text-muted-foreground mt-1">Add, edit, and manage your tour listings</p>
        </div>
        <Button onClick={handleCreate} className="rounded-full bg-primary" data-testid="create-tour-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add New Tour
        </Button>
      </div>

      <div className="grid gap-4">
        {tours.length === 0 ? (
          <Card className="p-12 text-center rounded-xl">
            <MapIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">No Tours Yet</h3>
            <p className="text-muted-foreground mb-4">Create your first tour to get started</p>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Tour
            </Button>
          </Card>
        ) : (
          tours.map((tour) => (
            <Card key={tour.id} className="p-4 rounded-xl hover:shadow-md transition-shadow" data-testid={`admin-tour-${tour.id}`}>
              <div className="flex items-center gap-4">
                <img 
                  src={tour.image_url} 
                  alt={tour.title}
                  className="w-24 h-24 object-cover rounded-lg"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-lg truncate">{tour.title}</h3>
                    <Badge className={tour.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {tour.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <span className="capitalize">{tour.category}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {tour.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {tour.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      {tour.rating}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-primary mt-1">{formatPrice(tour.price)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(tour)} className="gap-1">
                    <Edit2 className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onDelete(tour.id)} className="text-destructive hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      <TourFormModal 
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        tour={editingTour}
        onSuccess={() => {
          setModalOpen(false);
          onRefresh();
        }}
      />
    </div>
  );
}

// Bookings Tab Component
function BookingsTab({ bookings, onUpdateStatus, onRefresh }) {
  const [activeView, setActiveView] = useState('upcoming');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Calculate dates for filtering
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const nextWeek = new Date(today);
  nextWeek.setDate(nextWeek.getDate() + 7);
  
  // Filter bookings by view
  const filterBookings = (bookingList) => {
    return bookingList.filter(b => {
      const matchesSearch = searchTerm === '' || 
        b.tour_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.user_email?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  };
  
  const getBookingDate = (booking) => {
    const dateStr = booking.tour_date || booking.date;
    return dateStr ? new Date(dateStr) : null;
  };
  
  const isToday = (booking) => {
    const date = getBookingDate(booking);
    if (!date) return false;
    return date.toDateString() === today.toDateString();
  };
  
  const isTomorrow = (booking) => {
    const date = getBookingDate(booking);
    if (!date) return false;
    return date.toDateString() === tomorrow.toDateString();
  };
  
  const isThisWeek = (booking) => {
    const date = getBookingDate(booking);
    if (!date) return false;
    return date >= today && date <= nextWeek;
  };
  
  const todayBookings = filterBookings(bookings.filter(b => isToday(b) && b.status !== 'cancelled'));
  const tomorrowBookings = filterBookings(bookings.filter(b => isTomorrow(b) && b.status !== 'cancelled'));
  const weekBookings = filterBookings(bookings.filter(b => isThisWeek(b) && b.status !== 'cancelled'));
  const allBookings = filterBookings(bookings);
  
  const getDisplayBookings = () => {
    switch(activeView) {
      case 'today': return todayBookings;
      case 'tomorrow': return tomorrowBookings;
      case 'week': return weekBookings;
      default: return allBookings;
    }
  };

  const displayBookings = getDisplayBookings();

  return (
    <div data-testid="bookings-tab">
      {/* Header with Stats */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold">Bookings</h1>
          <p className="text-muted-foreground">Manage tour reservations and upcoming tours</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card 
          className={`p-4 cursor-pointer transition-all ${activeView === 'today' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}
          onClick={() => setActiveView('today')}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Bell className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{todayBookings.length}</p>
              <p className="text-sm text-muted-foreground">Today's Tours</p>
            </div>
          </div>
          {todayBookings.length > 0 && (
            <Badge className="mt-2 bg-red-500 text-white">Now</Badge>
          )}
        </Card>
        
        <Card 
          className={`p-4 cursor-pointer transition-all ${activeView === 'tomorrow' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}
          onClick={() => setActiveView('tomorrow')}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{tomorrowBookings.length}</p>
              <p className="text-sm text-muted-foreground">Tomorrow</p>
            </div>
          </div>
          {tomorrowBookings.length > 0 && (
            <Badge className="mt-2 bg-amber-500 text-white">Upcoming</Badge>
          )}
        </Card>
        
        <Card 
          className={`p-4 cursor-pointer transition-all ${activeView === 'week' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}
          onClick={() => setActiveView('week')}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{weekBookings.length}</p>
              <p className="text-sm text-muted-foreground">This Week</p>
            </div>
          </div>
        </Card>
        
        <Card 
          className={`p-4 cursor-pointer transition-all ${activeView === 'all' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}
          onClick={() => setActiveView('all')}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{bookings.length}</p>
              <p className="text-sm text-muted-foreground">All Bookings</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by tour, customer name, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Bookings Table */}
      {displayBookings.length === 0 ? (
        <Card className="p-12 text-center rounded-xl">
          <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">
            {activeView === 'today' ? 'No Tours Today' : 
             activeView === 'tomorrow' ? 'No Tours Tomorrow' :
             activeView === 'week' ? 'No Tours This Week' : 'No Bookings Yet'}
          </h3>
          <p className="text-muted-foreground">
            {searchTerm ? 'No bookings match your search' : 'Bookings will appear here when customers make reservations'}
          </p>
        </Card>
      ) : (
        <Card className="rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 font-medium">Tour</th>
                  <th className="text-left p-4 font-medium">Customer</th>
                  <th className="text-left p-4 font-medium">Date</th>
                  <th className="text-left p-4 font-medium">Guests</th>
                  <th className="text-left p-4 font-medium">Total</th>
                  <th className="text-left p-4 font-medium">Status</th>
                  <th className="text-left p-4 font-medium">Payment</th>
                  <th className="text-left p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayBookings.map((booking) => {
                  const bookingIsToday = isToday(booking);
                  const bookingIsTomorrow = isTomorrow(booking);
                  
                  return (
                    <tr 
                      key={booking.id} 
                      className={`border-t ${bookingIsToday ? 'bg-red-50' : bookingIsTomorrow ? 'bg-amber-50' : ''}`} 
                      data-testid={`admin-booking-${booking.id}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{booking.tour_title}</span>
                          {bookingIsToday && <Badge className="bg-red-500 text-white text-xs">Today</Badge>}
                          {bookingIsTomorrow && <Badge className="bg-amber-500 text-white text-xs">Tomorrow</Badge>}
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-sm">{booking.booker_name || booking.customer_name || booking.user_email?.split('@')[0] || 'Guest'}</p>
                          <p className="text-xs text-muted-foreground">{booking.booker_email || booking.user_email || booking.customer_email}</p>
                          {(booking.booker_phone || booking.customer_phone) && (
                            <p className="text-xs text-muted-foreground">{booking.booker_phone || booking.customer_phone}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-sm">{formatShortDate(booking.tour_date || booking.date)}</td>
                      <td className="p-4">{booking.guests}</td>
                      <td className="p-4 font-medium">{formatPrice(booking.total_price)}</td>
                      <td className="p-4">
                        <Badge className={getStatusColor(booking.status)}>
                          {booking.status}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge className={getPaymentStatusColor(booking.payment_status || 'pending')}>
                          {booking.payment_status || 'pending'}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-1">
                          {booking.status === 'pending' && (
                            <>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => onUpdateStatus(booking.id, 'confirmed')}
                                title="Confirm"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => onUpdateStatus(booking.id, 'cancelled')}
                                title="Cancel"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {booking.status === 'confirmed' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => onUpdateStatus(booking.id, 'completed')}
                            >
                              Complete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}


// SettingsTab is now imported from '../components/admin/SettingsTab'
// Badges Tab Component
function BadgesTab({ onRefresh }) {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingBadge, setEditingBadge] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    image_url: '',
    is_active: true,
  });

  const loadBadges = async () => {
    try {
      const response = await adminAPI.getAllBadges();
      setBadges(response.data || []);
    } catch (error) {
      console.error('Error loading badges:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBadges();
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        const response = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_data: base64 }),
        });
        
        if (!response.ok) throw new Error('Upload failed');
        
        const data = await response.json();
        const imageUrl = `${API_URL}${data.url}`;
        setFormData(prev => ({ ...prev, image_url: imageUrl }));
        toast.success('Image uploaded!');
        setUploading(false);
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.image_url) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      if (editingBadge) {
        await adminAPI.updateBadge(editingBadge.id, formData);
        toast.success('Badge updated!');
      } else {
        await adminAPI.createBadge(formData);
        toast.success('Badge created!');
      }
      setShowDialog(false);
      setEditingBadge(null);
      setFormData({ name: '', image_url: '', is_active: true });
      loadBadges();
    } catch (error) {
      console.error('Error saving badge:', error);
      toast.error('Failed to save badge');
    }
  };

  const handleEdit = (badge) => {
    setEditingBadge(badge);
    setFormData({
      name: badge.name,
      image_url: badge.image_url,
      is_active: badge.is_active,
    });
    setShowDialog(true);
  };

  const handleDelete = async (badgeId) => {
    if (!confirm('Are you sure you want to delete this badge?')) return;
    
    try {
      await adminAPI.deleteBadge(badgeId);
      toast.success('Badge deleted!');
      loadBadges();
    } catch (error) {
      console.error('Error deleting badge:', error);
      toast.error('Failed to delete badge');
    }
  };

  const openNewDialog = () => {
    setEditingBadge(null);
    setFormData({ name: '', image_url: '', is_active: true });
    setShowDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="badges-tab">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Trust Badges</h1>
          <p className="text-muted-foreground mt-1">Manage certifications and trust badges displayed on your site</p>
        </div>
        <Button onClick={openNewDialog} className="gap-2" data-testid="add-badge-btn">
          <Plus className="h-4 w-4" />
          Add Badge
        </Button>
      </div>

      {badges.length === 0 ? (
        <Card className="p-12 text-center">
          <Star className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">No badges yet</h3>
          <p className="text-muted-foreground mb-4">Add trust badges to build credibility with your customers</p>
          <Button onClick={openNewDialog}>Add Your First Badge</Button>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {badges.map((badge) => (
            <Card key={badge.id} className="p-6" data-testid={`badge-card-${badge.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="w-20 h-20 bg-muted rounded-lg flex items-center justify-center overflow-hidden">
                  <img 
                    src={badge.image_url} 
                    alt={badge.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={badge.is_active ? 'default' : 'secondary'}>
                    {badge.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
              <h3 className="font-semibold text-lg mb-2">{badge.name}</h3>
              <div className="flex items-center gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => handleEdit(badge)} data-testid={`edit-badge-${badge.id}`}>
                  <Edit2 className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDelete(badge.id)} data-testid={`delete-badge-${badge.id}`}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Badge Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingBadge ? 'Edit Badge' : 'Add New Badge'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Badge Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., TripAdvisor Certified"
                data-testid="badge-name-input"
              />
            </div>
            
            <div>
              <Label>Badge Image</Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-24 h-24 border-2 border-dashed border-border rounded-lg flex items-center justify-center overflow-hidden bg-muted/30">
                  {formData.image_url ? (
                    <img src={formData.image_url} alt="Badge preview" className="w-full h-full object-contain" />
                  ) : (
                    <Image className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="gap-2"
                    data-testid="upload-badge-image-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        Upload Image
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 5MB</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-muted-foreground">Show this badge on the website</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="badge-active-toggle"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-badge-btn">
              {editingBadge ? 'Update Badge' : 'Create Badge'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Categories Tab Component
function CategoriesTab({ onRefresh }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: 'Compass',
    color: '#22c55e',
    image_url: '',
    is_active: true,
    sort_order: 0,
  });

  const iconOptions = [
    { value: 'Compass', label: 'Compass' },
    { value: 'Mountain', label: 'Mountain' },
    { value: 'Waves', label: 'Waves' },
    { value: 'UtensilsCrossed', label: 'Utensils' },
    { value: 'Moon', label: 'Moon' },
    { value: 'Sun', label: 'Sun' },
    { value: 'Camera', label: 'Camera' },
    { value: 'Map', label: 'Map' },
    { value: 'Anchor', label: 'Anchor' },
    { value: 'TreePine', label: 'Tree' },
    { value: 'Bike', label: 'Bike' },
    { value: 'Car', label: 'Car' },
    { value: 'Plane', label: 'Plane' },
    { value: 'Tent', label: 'Tent' },
    { value: 'Sparkles', label: 'Sparkles' },
  ];

  const colorPresets = [
    '#22c55e', // Green
    '#06b6d4', // Cyan
    '#f97316', // Orange
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#eab308', // Yellow
    '#ef4444', // Red
    '#3b82f6', // Blue
    '#6b7280', // Gray
  ];

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await adminAPI.getAllCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      icon: category.icon || 'Compass',
      color: category.color || '#22c55e',
      image_url: category.image_url || '',
      is_active: category.is_active,
      sort_order: category.sort_order || 0,
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.slug) {
      toast.error('Name and slug are required');
      return;
    }

    try {
      if (editingCategory) {
        await adminAPI.updateCategory(editingCategory.id, formData);
        toast.success('Category updated!');
      } else {
        await adminAPI.createCategory(formData);
        toast.success('Category created!');
      }
      setShowDialog(false);
      setEditingCategory(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        icon: 'Compass',
        color: '#22c55e',
        image_url: '',
        is_active: true,
        sort_order: 0,
      });
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await adminAPI.deleteCategory(categoryId);
      toast.success('Category deleted');
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const handleToggle = async (categoryId) => {
    try {
      const response = await adminAPI.toggleCategory(categoryId);
      toast.success(`Category ${response.data.is_active ? 'enabled' : 'disabled'}`);
      loadCategories();
    } catch (error) {
      toast.error('Failed to toggle category');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const response = await adminAPI.uploadImage(file);
      setFormData({ ...formData, image_url: response.data.url });
      toast.success('Image uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  return (
    <div data-testid="categories-tab">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold">Categories</h1>
          <p className="text-muted-foreground mt-1">Manage tour categories for better organization</p>
        </div>
        <Button 
          onClick={() => {
            setEditingCategory(null);
            setFormData({
              name: '',
              slug: '',
              description: '',
              icon: 'Compass',
              color: '#22c55e',
              is_active: true,
              sort_order: categories.length + 1,
            });
            setShowDialog(true);
          }} 
          className="rounded-full bg-primary"
          data-testid="add-category-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Category
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : categories.length === 0 ? (
        <Card className="p-12 text-center rounded-xl">
          <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">No categories yet</h3>
          <p className="text-muted-foreground mb-4">Create categories to organize your tours</p>
          <Button onClick={() => setShowDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add First Category
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {categories.map((category, index) => (
            <Card 
              key={category.id} 
              className={`p-4 ${!category.is_active ? 'opacity-60' : ''}`}
              data-testid={`category-${category.slug}`}
            >
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <GripVertical className="h-5 w-5 cursor-move" />
                  <span className="text-sm font-medium w-6">{index + 1}</span>
                </div>
                
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${category.color}20`, color: category.color }}
                >
                  <FolderOpen className="h-5 w-5" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{category.name}</h3>
                    <Badge 
                      className="text-xs"
                      style={{ backgroundColor: `${category.color}20`, color: category.color }}
                    >
                      {category.slug}
                    </Badge>
                    {!category.is_active && (
                      <Badge variant="outline" className="text-xs">Disabled</Badge>
                    )}
                  </div>
                  {category.description && (
                    <p className="text-sm text-muted-foreground">{category.description}</p>
                  )}
                </div>
                
                <div className="text-center px-4">
                  <p className="text-2xl font-bold" style={{ color: category.color }}>{category.tour_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Tours</p>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleToggle(category.id)}
                  >
                    {category.is_active ? 'Disable' : 'Enable'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEdit(category)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(category.id)}
                    disabled={category.tour_count > 0}
                    title={category.tour_count > 0 ? 'Cannot delete category with tours' : 'Delete category'}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Category Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Category Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setFormData({ 
                    ...formData, 
                    name,
                    slug: editingCategory ? formData.slug : generateSlug(name)
                  });
                }}
                placeholder="e.g., Adventure Tours"
                data-testid="category-name-input"
              />
            </div>

            <div>
              <Label>Slug *</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: generateSlug(e.target.value) })}
                placeholder="e.g., adventure"
                data-testid="category-slug-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                URL-friendly identifier. Used in filters and URLs.
              </p>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this category..."
                rows={2}
                data-testid="category-description-input"
              />
            </div>

            <div>
              <Label>Icon</Label>
              <Select 
                value={formData.icon} 
                onValueChange={(value) => setFormData({ ...formData, icon: value })}
              >
                <SelectTrigger data-testid="category-icon-select">
                  <SelectValue placeholder="Select icon" />
                </SelectTrigger>
                <SelectContent>
                  {iconOptions.map(icon => (
                    <SelectItem key={icon.value} value={icon.value}>
                      {icon.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Color</Label>
              <div className="flex gap-2 mt-2">
                {colorPresets.map(color => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      formData.color === color ? 'border-foreground scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData({ ...formData, color })}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Palette className="h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  placeholder="#22c55e"
                  className="w-28 font-mono text-sm"
                  data-testid="category-color-input"
                />
                <div 
                  className="w-8 h-8 rounded border"
                  style={{ backgroundColor: formData.color }}
                />
              </div>
            </div>

            <div>
              <Label>Category Image</Label>
              <div className="mt-2">
                {formData.image_url ? (
                  <div className="relative w-full h-32 rounded-lg overflow-hidden border">
                    <img 
                      src={formData.image_url} 
                      alt="Category preview" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="category-image-upload"
                    />
                    <label 
                      htmlFor="category-image-upload"
                      className="flex flex-col items-center cursor-pointer"
                    >
                      <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground">Click to upload image</span>
                    </label>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Or paste image URL:
                </p>
                <Input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  className="mt-1"
                  data-testid="category-image-url-input"
                />
              </div>
            </div>

            <div>
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                min={0}
                data-testid="category-order-input"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="category-active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="category-active">Active (visible on website)</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-category-btn">
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Quick Searches Tab Component
function QuickSearchesTab({ onRefresh }) {
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSearch, setEditingSearch] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    query: '',
    category: '',
    is_active: true,
    sort_order: 0,
  });

  const categories = [
    { value: '', label: 'All Categories' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'marine', label: 'Marine' },
    { value: 'food', label: 'Food & Culture' },
    { value: 'night', label: 'Night Tours' },
  ];

  const loadSearches = async () => {
    try {
      const response = await adminAPI.getAllQuickSearches();
      setSearches(response.data || []);
    } catch (error) {
      console.error('Error loading quick searches:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSearches();
  }, []);

  const handleSubmit = async () => {
    if (!formData.name || !formData.query) {
      toast.error('Please fill in name and search query');
      return;
    }

    try {
      if (editingSearch) {
        await adminAPI.updateQuickSearch(editingSearch.id, formData);
        toast.success('Quick search updated!');
      } else {
        await adminAPI.createQuickSearch(formData);
        toast.success('Quick search created!');
      }
      setShowDialog(false);
      setEditingSearch(null);
      setFormData({ name: '', query: '', category: '', is_active: true, sort_order: 0 });
      loadSearches();
    } catch (error) {
      console.error('Error saving quick search:', error);
      toast.error('Failed to save quick search');
    }
  };

  const handleEdit = (search) => {
    setEditingSearch(search);
    setFormData({
      name: search.name,
      query: search.query,
      category: search.category || '',
      is_active: search.is_active,
      sort_order: search.sort_order || 0,
    });
    setShowDialog(true);
  };

  const handleDelete = async (searchId) => {
    if (!confirm('Are you sure you want to delete this quick search?')) return;
    
    try {
      await adminAPI.deleteQuickSearch(searchId);
      toast.success('Quick search deleted!');
      loadSearches();
    } catch (error) {
      console.error('Error deleting quick search:', error);
      toast.error('Failed to delete quick search');
    }
  };

  const openNewDialog = () => {
    setEditingSearch(null);
    setFormData({ name: '', query: '', category: '', is_active: true, sort_order: searches.length });
    setShowDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="quick-searches-tab">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Quick Searches</h1>
          <p className="text-muted-foreground mt-1">Manage quick search tags displayed on the homepage</p>
        </div>
        <Button onClick={openNewDialog} className="gap-2" data-testid="add-quick-search-btn">
          <Plus className="h-4 w-4" />
          Add Quick Search
        </Button>
      </div>

      {searches.length === 0 ? (
        <Card className="p-12 text-center">
          <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">No quick searches yet</h3>
          <p className="text-muted-foreground mb-4">Add quick search tags to help customers find tours faster</p>
          <Button onClick={openNewDialog}>Add Your First Quick Search</Button>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {searches.map((search) => (
            <Card key={search.id} className="p-4" data-testid={`quick-search-card-${search.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-lg">{search.name}</h3>
                  <p className="text-sm text-muted-foreground">Query: "{search.query}"</p>
                </div>
                <Badge variant={search.is_active ? 'default' : 'secondary'}>
                  {search.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              {search.category && (
                <Badge variant="outline" className="mb-3 capitalize">
                  {search.category}
                </Badge>
              )}
              <div className="flex items-center gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={() => handleEdit(search)}>
                  <Edit2 className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDelete(search.id)}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Quick Search Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSearch ? 'Edit Quick Search' : 'Add New Quick Search'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Display Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Bio Bay"
                data-testid="quick-search-name-input"
              />
              <p className="text-xs text-muted-foreground mt-1">The text shown on the tag</p>
            </div>
            
            <div>
              <Label>Search Query</Label>
              <Input
                value={formData.query}
                onChange={(e) => setFormData({ ...formData, query: e.target.value })}
                placeholder="e.g., bioluminescent"
                data-testid="quick-search-query-input"
              />
              <p className="text-xs text-muted-foreground mt-1">The search term used when clicked</p>
            </div>

            <div>
              <Label>Category Filter (Optional)</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
                data-testid="quick-search-category-select"
              >
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">If set, clicking will filter by this category</p>
            </div>

            <div>
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                placeholder="0"
                data-testid="quick-search-order-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Lower numbers appear first</p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-muted-foreground">Show on homepage</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="quick-search-active-toggle"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-quick-search-btn">
              {editingSearch ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// POS Tab Component
function POSTab({ onRefresh }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [tours, setTours] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [pendingPaymentId, setPendingPaymentId] = useState(null);
  const [formData, setFormData] = useState({
    tour_id: '',
    tour_title: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    amount: '',
    guests: 1,
    tour_date: '',
    notes: ''
  });

  useEffect(() => {
    loadPayments();
    loadTours();
  }, []);

  // Auto-poll for pending payment status
  useEffect(() => {
    if (!pendingPaymentId) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await adminAPI.checkPOSPaymentStatus(pendingPaymentId);
        if (response.data.status === 'paid') {
          toast.success('Payment confirmed! Confirmation email sent to customer.');
          setPendingPaymentId(null);
          loadPayments();
        }
      } catch (error) {
        console.error('Error polling payment status:', error);
      }
    }, 3000); // Poll every 3 seconds
    
    return () => clearInterval(pollInterval);
  }, [pendingPaymentId]);

  const loadPayments = async () => {
    try {
      const response = await adminAPI.getAllPOSPayments();
      setPayments(response.data || []);
    } catch (error) {
      console.error('Error loading POS payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const loadTours = async () => {
    try {
      const response = await adminAPI.getAllTours();
      setTours(response.data || []);
    } catch (error) {
      console.error('Error loading tours:', error);
    }
  };

  const handleTourSelect = (tourId) => {
    const tour = tours.find(t => t.id === tourId);
    if (tour) {
      setFormData({
        ...formData,
        tour_id: tourId,
        tour_title: tour.title,
        amount: tour.price.toString()
      });
    }
  };

  const handleSubmit = async () => {
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone || !formData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await adminAPI.createPOSPayment({
        ...formData,
        amount: parseFloat(formData.amount),
        guests: parseInt(formData.guests)
      });
      
      // Set pending payment for auto-polling
      setPendingPaymentId(response.data.id);
      
      toast.success('Payment created! Opening secure checkout for customer card entry...');
      toast.info('The page will auto-update when payment is complete.');
      
      // Open Stripe checkout in new tab
      if (response.data.checkout_url) {
        window.open(response.data.checkout_url, '_blank');
      }
      
      setShowDialog(false);
      setFormData({
        tour_id: '',
        tour_title: '',
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        amount: '',
        guests: 1,
        tour_date: '',
        notes: ''
      });
      loadPayments();
    } catch (error) {
      toast.error('Failed to create payment');
    } finally {
      setSubmitting(false);
    }
  };

  const checkPaymentStatus = async (paymentId) => {
    try {
      const response = await adminAPI.checkPOSPaymentStatus(paymentId);
      if (response.data.status === 'paid') {
        toast.success('Payment confirmed!');
        loadPayments();
      } else {
        toast.info(`Payment status: ${response.data.status}`);
      }
    } catch (error) {
      toast.error('Failed to check payment status');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div data-testid="pos-tab">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold">POS Payments</h1>
          <p className="text-muted-foreground mt-1">Process phone payments for customers</p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="rounded-full bg-primary" data-testid="new-pos-payment-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Payment
        </Button>
      </div>

      {/* Pending Payment Banner */}
      {pendingPaymentId && (
        <Card className="p-4 mb-6 bg-yellow-50 border-yellow-200">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-yellow-600" />
            <div className="flex-1">
              <p className="font-medium text-yellow-800">Waiting for customer payment...</p>
              <p className="text-sm text-yellow-600">Enter card details in the checkout window. This page will auto-update when payment is complete.</p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setPendingPaymentId(null)}
            >
              Dismiss
            </Button>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : payments.length === 0 ? (
        <Card className="p-12 text-center rounded-xl">
          <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">No POS payments yet</h3>
          <p className="text-muted-foreground mb-4">Process phone orders for customers here</p>
          <Button onClick={() => setShowDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create First Payment
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {payments.map((payment) => (
            <Card key={payment.id} className="p-4" data-testid={`pos-payment-${payment.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-lg">{payment.customer_name}</h3>
                    <Badge className={getStatusBadge(payment.payment_status)}>
                      {payment.payment_status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{payment.customer_email} • {payment.customer_phone}</p>
                  <p className="text-sm font-medium mt-1">{payment.tour_title}</p>
                  {payment.tour_date && <p className="text-xs text-muted-foreground">Date: {payment.tour_date}</p>}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary">${payment.amount.toFixed(2)}</p>
                  <p className="text-sm text-muted-foreground">{payment.guests} guest(s)</p>
                  {payment.payment_status === 'pending' && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => checkPaymentStatus(payment.id)}
                    >
                      Check Status
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* New Payment Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Phone Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label>Select Tour (Optional)</Label>
              <Select value={formData.tour_id} onValueChange={handleTourSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a tour..." />
                </SelectTrigger>
                <SelectContent>
                  {tours.map((tour) => (
                    <SelectItem key={tour.id} value={tour.id}>
                      {tour.title} - ${tour.price}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Tour/Service Name *</Label>
              <Input
                value={formData.tour_title}
                onChange={(e) => setFormData({ ...formData, tour_title: e.target.value })}
                placeholder="e.g., El Yunque Rainforest Adventure"
                data-testid="pos-tour-title"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (USD) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="89.00"
                  data-testid="pos-amount"
                />
              </div>
              <div>
                <Label>Number of Guests</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.guests}
                  onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                  data-testid="pos-guests"
                />
              </div>
            </div>

            <div>
              <Label>Customer Name *</Label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                placeholder="John Doe"
                data-testid="pos-customer-name"
              />
            </div>

            <div>
              <Label>Customer Email *</Label>
              <Input
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                placeholder="john@example.com"
                data-testid="pos-customer-email"
              />
            </div>

            <div>
              <Label>Customer Phone *</Label>
              <Input
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                placeholder="+1 (787) 555-0123"
                data-testid="pos-customer-phone"
              />
            </div>

            <div>
              <Label>Tour Date</Label>
              <Input
                type="date"
                value={formData.tour_date}
                onChange={(e) => setFormData({ ...formData, tour_date: e.target.value })}
                data-testid="pos-tour-date"
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Special requests or notes..."
                data-testid="pos-notes"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="create-pos-payment-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
              Process Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Invoices Tab Component
function InvoicesTab({ onRefresh }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [tours, setTours] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    tour_id: '',
    tour_title: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    amount: '',
    guests: 1,
    tour_date: '',
    notes: '',
    due_date: ''
  });

  useEffect(() => {
    loadInvoices();
    loadTours();
  }, []);

  const loadInvoices = async () => {
    try {
      const response = await adminAPI.getAllInvoices();
      setInvoices(response.data || []);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const loadTours = async () => {
    try {
      const response = await adminAPI.getAllTours();
      setTours(response.data || []);
    } catch (error) {
      console.error('Error loading tours:', error);
    }
  };

  const handleTourSelect = (tourId) => {
    const tour = tours.find(t => t.id === tourId);
    if (tour) {
      setFormData({
        ...formData,
        tour_id: tourId,
        tour_title: tour.title,
        amount: tour.price.toString()
      });
    }
  };

  const handleSubmit = async () => {
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone || !formData.amount || !formData.tour_title) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await adminAPI.createInvoice({
        ...formData,
        amount: parseFloat(formData.amount),
        guests: parseInt(formData.guests)
      });
      
      toast.success('Invoice created!');
      setShowDialog(false);
      setFormData({
        tour_id: '',
        tour_title: '',
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        amount: '',
        guests: 1,
        tour_date: '',
        notes: '',
        due_date: ''
      });
      loadInvoices();
    } catch (error) {
      toast.error('Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await adminAPI.sendInvoice(invoiceId);
      toast.success('Invoice sent to customer!');
      loadInvoices();
    } catch (error) {
      toast.error('Failed to send invoice');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await adminAPI.deleteInvoice(invoiceId);
      toast.success('Invoice deleted');
      loadInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const copyPaymentLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Payment link copied!');
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div data-testid="invoices-tab">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold">Invoices</h1>
          <p className="text-muted-foreground mt-1">Send payment links to customers</p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="rounded-full bg-primary" data-testid="new-invoice-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Invoice
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : invoices.length === 0 ? (
        <Card className="p-12 text-center rounded-xl">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">No invoices yet</h3>
          <p className="text-muted-foreground mb-4">Create invoices with unique payment links</p>
          <Button onClick={() => setShowDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create First Invoice
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {invoices.map((invoice) => (
            <Card key={invoice.id} className="p-4" data-testid={`invoice-${invoice.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-lg">{invoice.invoice_number}</h3>
                    <Badge className={getStatusBadge(invoice.payment_status)}>
                      {invoice.payment_status}
                    </Badge>
                  </div>
                  <p className="font-medium">{invoice.customer_name}</p>
                  <p className="text-sm text-muted-foreground">{invoice.customer_email} • {invoice.customer_phone}</p>
                  <p className="text-sm mt-1">{invoice.tour_title}</p>
                  {invoice.tour_date && <p className="text-xs text-muted-foreground">Date: {invoice.tour_date}</p>}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary">${invoice.amount.toFixed(2)}</p>
                  <p className="text-sm text-muted-foreground">{invoice.guests} guest(s)</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                <div className="flex-1 flex items-center gap-2">
                  <code className="text-xs bg-muted px-2 py-1 rounded flex-1 truncate">
                    {invoice.payment_link}
                  </code>
                  <Button variant="ghost" size="icon" onClick={() => copyPaymentLink(invoice.payment_link)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => window.open(invoice.payment_link, '_blank')}>
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
                {invoice.payment_status === 'pending' && (
                  <Button variant="outline" size="sm" onClick={() => handleSendInvoice(invoice.id)}>
                    <Send className="h-4 w-4 mr-1" />
                    Send
                  </Button>
                )}
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDeleteInvoice(invoice.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* New Invoice Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label>Select Tour (Optional)</Label>
              <Select value={formData.tour_id} onValueChange={handleTourSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a tour..." />
                </SelectTrigger>
                <SelectContent>
                  {tours.map((tour) => (
                    <SelectItem key={tour.id} value={tour.id}>
                      {tour.title} - ${tour.price}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Tour/Service Name *</Label>
              <Input
                value={formData.tour_title}
                onChange={(e) => setFormData({ ...formData, tour_title: e.target.value })}
                placeholder="e.g., El Yunque Rainforest Adventure"
                data-testid="invoice-tour-title"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (USD) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="89.00"
                  data-testid="invoice-amount"
                />
              </div>
              <div>
                <Label>Number of Guests</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.guests}
                  onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                  data-testid="invoice-guests"
                />
              </div>
            </div>

            <div>
              <Label>Customer Name *</Label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                placeholder="John Doe"
                data-testid="invoice-customer-name"
              />
            </div>

            <div>
              <Label>Customer Email *</Label>
              <Input
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                placeholder="john@example.com"
                data-testid="invoice-customer-email"
              />
            </div>

            <div>
              <Label>Customer Phone *</Label>
              <Input
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                placeholder="+1 (787) 555-0123"
                data-testid="invoice-customer-phone"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tour Date</Label>
                <Input
                  type="date"
                  value={formData.tour_date}
                  onChange={(e) => setFormData({ ...formData, tour_date: e.target.value })}
                  data-testid="invoice-tour-date"
                />
              </div>
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  data-testid="invoice-due-date"
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Special requests or notes..."
                data-testid="invoice-notes"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="create-invoice-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
              Create Invoice
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Email Templates Tab Component
function EmailTemplatesTab({ onRefresh }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState({ subject: '', body: '' });
  const [formData, setFormData] = useState({
    subject: '',
    body: ''
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await adminAPI.getAllEmailTemplates();
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error loading email templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      subject: template.subject,
      body: template.body
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!editingTemplate) return;
    
    try {
      await adminAPI.updateEmailTemplate(editingTemplate.template_type, formData);
      toast.success('Template updated!');
      setShowDialog(false);
      setEditingTemplate(null);
      loadTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handlePreview = async (templateType) => {
    try {
      const response = await adminAPI.previewEmailTemplate(templateType);
      setPreviewContent(response.data);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const getTemplateLabel = (type) => {
    const labels = {
      booking_confirmation: 'Booking Confirmation',
      invoice: 'Invoice',
      payment_receipt: 'Payment Receipt',
      tour_reminder: 'Tour Reminder (24hr)',
      waiver_reminder: 'Waiver Reminder',
      thank_you: 'Thank You / Review Request',
      payment_reminder: 'Payment Reminder'
    };
    return labels[type] || type;
  };

  const getTemplateDescription = (type) => {
    const descriptions = {
      booking_confirmation: 'Sent automatically when a booking is confirmed',
      invoice: 'Sent when an invoice is created for a customer',
      payment_receipt: 'Sent automatically after successful payment',
      tour_reminder: 'Sent automatically 24 hours before the tour',
      waiver_reminder: 'Sent to customers who haven\'t signed their waiver',
      thank_you: 'Sent after tour completion to request reviews',
      payment_reminder: 'Sent to remind customers of pending payments'
    };
    return descriptions[type] || '';
  };

  const isAutomatic = (type) => {
    return ['booking_confirmation', 'payment_receipt', 'tour_reminder'].includes(type);
  };

  return (
    <div data-testid="email-templates-tab">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold">Email Templates</h1>
        <p className="text-muted-foreground mt-1">Customize automated emails sent to customers</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-4">
          {templates.map((template) => (
            <Card key={template.id} className="p-6" data-testid={`template-${template.template_type}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">{getTemplateLabel(template.template_type)}</h3>
                    {isAutomatic(template.template_type) && (
                      <Badge className="bg-green-100 text-green-800 text-xs">Auto-send</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{getTemplateDescription(template.template_type)}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    <span className="font-medium">Subject:</span> {template.subject}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handlePreview(template.template_type)}>
                    <Eye className="h-4 w-4 mr-1" /> Preview
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(template)}>
                    <Edit2 className="h-4 w-4 mr-1" /> Edit
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Template Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit {editingTemplate && getTemplateLabel(editingTemplate.template_type)}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            <div>
              <Label>Subject Line</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Email subject..."
                data-testid="template-subject"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Variables: {'{{customer_name}}'}, {'{{tour_title}}'}, {'{{tour_date}}'}, {'{{amount}}'}
              </p>
            </div>

            <div>
              <Label>Email Body</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Use the toolbar to format your email. Variables will be replaced with actual data when sent.
              </p>
              <RichTextEditor
                value={formData.body}
                onChange={(body) => setFormData({ ...formData, body })}
                placeholder="Write your email content..."
              />
              <div className="mt-2 p-2 bg-muted/50 rounded text-xs">
                <p className="font-medium mb-1">Available Variables:</p>
                <div className="flex flex-wrap gap-2">
                  {['{{customer_name}}', '{{tour_title}}', '{{tour_date}}', '{{tour_time}}', '{{guests}}', '{{amount}}', '{{invoice_number}}', '{{payment_link}}', '{{payment_date}}', '{{phone_number}}', '{{waiver_status}}'].map(v => (
                    <code key={v} className="bg-white px-1.5 py-0.5 rounded border text-xs">{v}</code>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-template-btn">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted rounded">
              <p className="text-sm"><span className="font-medium">Subject:</span> {previewContent.subject}</p>
            </div>
            <div className="border rounded p-4 bg-white">
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewContent.body) }} />
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// SMS Templates Tab Component
function SMSTemplatesTab({ onRefresh }) {
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [previewContent, setPreviewContent] = useState({ message: '', character_count: 0 });
  const [formData, setFormData] = useState({ message: '' });
  const [testPhone, setTestPhone] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await adminAPI.getAllSMSTemplates();
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error loading SMS templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const response = await adminAPI.getSMSLogs();
      setLogs(response.data || []);
      setShowLogs(true);
    } catch (error) {
      toast.error('Failed to load SMS logs');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({ message: template.message });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!editingTemplate) return;
    
    try {
      await adminAPI.updateSMSTemplate(editingTemplate.template_type, formData);
      toast.success('Template updated!');
      setShowDialog(false);
      setEditingTemplate(null);
      loadTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handleToggle = async (templateType) => {
    try {
      const response = await adminAPI.toggleSMSTemplate(templateType);
      toast.success(`SMS ${response.data.is_active ? 'enabled' : 'disabled'}`);
      loadTemplates();
    } catch (error) {
      toast.error('Failed to toggle template');
    }
  };

  const handlePreview = async (templateType) => {
    try {
      const response = await adminAPI.previewSMSTemplate(templateType);
      setPreviewContent(response.data);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const handleSendTest = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    try {
      await adminAPI.sendTestSMS(testPhone);
      toast.success('Test SMS sent');
      setTestPhone('');
    } catch (error) {
      toast.error('Failed to send test SMS');
    }
  };

  const getTemplateLabel = (type) => {
    const labels = {
      booking_confirmation: 'Booking Confirmation',
      invoice_sent: 'Invoice Sent',
      payment_receipt: 'Payment Receipt'
    };
    return labels[type] || type;
  };

  const getTemplateDescription = (type) => {
    const descriptions = {
      booking_confirmation: 'Sent after a successful booking',
      invoice_sent: 'Sent when an invoice is shared with a customer',
      payment_receipt: 'Sent after any payment is received'
    };
    return descriptions[type] || '';
  };

  return (
    <div data-testid="sms-templates-tab">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold">SMS Templates</h1>
          <p className="text-muted-foreground mt-1">Customize automated SMS sent to customers</p>
          <p className="text-sm text-yellow-600 mt-2 flex items-center gap-1">
            <AlertCircle className="h-4 w-4" />
            SMS will be sent via Twilio when credentials are configured
          </p>
        </div>
        <Button variant="outline" onClick={loadLogs} data-testid="view-sms-logs-btn">
          <Eye className="h-4 w-4 mr-2" />
          View SMS Logs
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-4">
          {templates.map((template) => (
            <Card key={template.id} className="p-6" data-testid={`sms-template-${template.template_type}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-lg">{getTemplateLabel(template.template_type)}</h3>
                    <Badge className={template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {template.is_active ? 'Active' : 'Disabled'}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{getTemplateDescription(template.template_type)}</p>
                  <p className="text-sm mt-2 bg-muted p-2 rounded font-mono text-xs">
                    {template.message.length > 100 ? template.message.substring(0, 100) + '...' : template.message}
                  </p>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleToggle(template.template_type)}
                  >
                    {template.is_active ? 'Disable' : 'Enable'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handlePreview(template.template_type)}>
                    <Eye className="h-4 w-4 mr-1" />
                    Preview
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(template)}>
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                </div>
              </div>
            </Card>
          ))}

          {/* Test SMS Section */}
          <Card className="p-6 mt-8 border-dashed">
            <h3 className="font-semibold mb-4">Send Test SMS</h3>
            <div className="flex gap-2">
              <Input
                type="tel"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+1 (787) 555-0123"
                className="max-w-xs"
                data-testid="test-sms-phone"
              />
              <Button onClick={handleSendTest} data-testid="send-test-sms-btn">
                <Send className="h-4 w-4 mr-2" />
                Send Test
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              This will log a test SMS to the database (not actually sent)
            </p>
          </Card>
        </div>
      )}

      {/* Edit Template Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit {editingTemplate && getTemplateLabel(editingTemplate.template_type)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>SMS Message</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="SMS message..."
                className="min-h-[120px]"
                data-testid="sms-template-message"
              />
              <div className="flex justify-between mt-1">
                <p className="text-xs text-muted-foreground">
                  Variables: {'{{customer_name}}'}, {'{{tour_title}}'}, {'{{tour_date}}'}, {'{{amount}}'}, {'{{payment_link}}'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formData.message.length}/160 characters
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-sms-template-btn">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>SMS Preview</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <MessageSquare className="h-5 w-5 text-green-600 mt-1" />
                <div>
                  <p className="text-sm">{previewContent.message}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {previewContent.character_count} characters
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SMS Logs Dialog */}
      <Dialog open={showLogs} onOpenChange={setShowLogs}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>SMS Logs</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-4 max-h-[50vh] overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No SMS logs yet</p>
            ) : (
              logs.map((log) => (
                <div key={log.id} className="border rounded p-3 text-sm">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium">{log.phone_number}</span>
                    <Badge className="bg-yellow-100 text-yellow-800">{log.status}</Badge>
                  </div>
                  <p className="text-muted-foreground text-xs mb-1">{log.template_type}</p>
                  <p className="bg-muted p-2 rounded text-xs">{log.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(log.created_at).toLocaleString()}
                  </p>
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowLogs(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Inbox Section Component - Conversation-style view of communications
function InboxSection({ emailLogs, smsLogs, customers, onSendReply }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  
  // Group messages by customer (email or phone)
  const conversations = React.useMemo(() => {
    const convMap = new Map();
    
    // Process email logs
    emailLogs.forEach(log => {
      const key = log.email_address || log.recipient;
      if (!convMap.has(key)) {
        convMap.set(key, {
          id: key,
          type: 'email',
          contact: key,
          name: customers.find(c => c.email === key)?.name || key.split('@')[0],
          messages: [],
          lastMessageDate: null,
        });
      }
      const conv = convMap.get(key);
      conv.messages.push({
        ...log,
        messageType: 'email',
        date: new Date(log.created_at),
      });
      if (!conv.lastMessageDate || new Date(log.created_at) > conv.lastMessageDate) {
        conv.lastMessageDate = new Date(log.created_at);
      }
    });
    
    // Process SMS logs
    smsLogs.forEach(log => {
      const key = log.phone_number || log.recipient;
      if (!convMap.has(key)) {
        convMap.set(key, {
          id: key,
          type: 'sms',
          contact: key,
          name: customers.find(c => c.phone === key)?.name || 'Customer',
          messages: [],
          lastMessageDate: null,
        });
      }
      const conv = convMap.get(key);
      conv.messages.push({
        ...log,
        messageType: 'sms',
        date: new Date(log.created_at),
      });
      if (!conv.lastMessageDate || new Date(log.created_at) > conv.lastMessageDate) {
        conv.lastMessageDate = new Date(log.created_at);
      }
    });
    
    // Sort messages within each conversation
    convMap.forEach(conv => {
      conv.messages.sort((a, b) => b.date - a.date);
    });
    
    // Convert to array and sort by last message date
    return Array.from(convMap.values())
      .filter(conv => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return conv.contact.toLowerCase().includes(search) || 
               conv.name.toLowerCase().includes(search) ||
               conv.messages.some(m => (m.subject || m.message || '').toLowerCase().includes(search));
      })
      .sort((a, b) => b.lastMessageDate - a.lastMessageDate);
  }, [emailLogs, smsLogs, customers, searchTerm]);

  const formatDate = (date) => {
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="inbox-section">
      {/* Conversation List */}
      <Card className="lg:col-span-1 p-4">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No conversations yet</p>
            </div>
          ) : (
            conversations.map(conv => (
              <button
                key={conv.id}
                onClick={() => setSelectedConversation(conv)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  selectedConversation?.id === conv.id
                    ? 'bg-primary/10 border-primary'
                    : 'hover:bg-muted/50 border-border'
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                      conv.type === 'email' ? 'bg-blue-500' : 'bg-green-500'
                    }`}>
                      {conv.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{conv.name}</p>
                      <p className="text-xs text-muted-foreground truncate max-w-[150px]">{conv.contact}</p>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(conv.lastMessageDate)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground truncate ml-10">
                  {conv.messages[0]?.subject || conv.messages[0]?.message || 'No message'}
                </p>
                <div className="flex gap-1 mt-1 ml-10">
                  <Badge variant="outline" className="text-xs">
                    {conv.type === 'email' ? <Mail className="h-3 w-3 mr-1" /> : <MessageSquare className="h-3 w-3 mr-1" />}
                    {conv.messages.length}
                  </Badge>
                </div>
              </button>
            ))
          )}
        </div>
      </Card>

      {/* Conversation Detail */}
      <Card className="lg:col-span-2 p-4">
        {selectedConversation ? (
          <>
            <div className="flex items-center justify-between pb-4 border-b mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-lg font-bold ${
                  selectedConversation.type === 'email' ? 'bg-blue-500' : 'bg-green-500'
                }`}>
                  {selectedConversation.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="font-semibold">{selectedConversation.name}</h3>
                  <p className="text-sm text-muted-foreground">{selectedConversation.contact}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onSendReply(selectedConversation.contact, selectedConversation.name)}
                  className="gap-1"
                >
                  <Send className="h-4 w-4" />
                  Reply
                </Button>
                {selectedConversation.type === 'sms' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.location.href = `tel:${selectedConversation.contact}`}
                    className="gap-1"
                  >
                    <Phone className="h-4 w-4" />
                    Call
                  </Button>
                )}
              </div>
            </div>
            
            <div className="space-y-4 max-h-[400px] overflow-y-auto">
              {selectedConversation.messages.map((msg, idx) => (
                <div key={idx} className="p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {msg.messageType === 'email' ? (
                        <Mail className="h-4 w-4 text-blue-500" />
                      ) : (
                        <MessageSquare className="h-4 w-4 text-green-500" />
                      )}
                      <span className="text-xs font-medium text-muted-foreground">
                        {msg.template_type?.replace(/_/g, ' ') || 'Custom Message'}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(msg.created_at).toLocaleString()}
                    </span>
                  </div>
                  {msg.subject && (
                    <p className="font-semibold text-sm mb-1">Subject: {msg.subject}</p>
                  )}
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {msg.message || msg.body || 'No content'}
                  </p>
                  <Badge className="mt-2 text-xs" variant={msg.status === 'sent' ? 'default' : 'secondary'}>
                    {msg.status || 'sent'}
                  </Badge>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-[400px] text-muted-foreground">
            <div className="text-center">
              <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">Select a conversation</p>
              <p className="text-sm">Choose a conversation from the list to view messages</p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// MessagingCenterTab is now imported from '../components/admin/MessagingCenterTab'

// UsersTab is now imported from '../components/admin/UsersTab'
// FooterTab is now imported from '../components/admin/FooterTab'
// Tour Availability Section Component (inside Tour Edit Modal) - Now using Calendar
function TourAvailabilitySection({ tourId, tourTitle }) {
  return <TourAvailabilityCalendar tourId={tourId} tourTitle={tourTitle} />;
}

// Custom Pages Tab Component
function CustomPagesTab({ onRefresh }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    meta_description: '',
    is_active: true,
  });

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      const response = await adminAPI.getAllPages();
      setPages(response.data || []);
    } catch (error) {
      console.error('Error loading pages:', error);
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (page = null) => {
    if (page) {
      setEditingPage(page);
      setFormData({
        title: page.title,
        slug: page.slug,
        content: page.content,
        meta_description: page.meta_description || '',
        is_active: page.is_active,
      });
    } else {
      setEditingPage(null);
      setFormData({
        title: '',
        slug: '',
        content: '',
        meta_description: '',
        is_active: true,
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPage) {
        await adminAPI.updatePage(editingPage.id, formData);
        toast.success('Page updated successfully!');
      } else {
        await adminAPI.createPage(formData);
        toast.success('Page created successfully!');
      }
      setShowDialog(false);
      loadPages();
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error(error.response?.data?.detail || 'Failed to save page');
    }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;
    try {
      await adminAPI.deletePage(pageId);
      toast.success('Page deleted successfully!');
      loadPages();
    } catch (error) {
      toast.error('Failed to delete page');
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Page templates for quick start
  const pageTemplates = [
    {
      name: 'Privacy Policy',
      slug: 'privacy-policy',
      content: `<h2>Privacy Policy</h2>
<p>Last updated: ${new Date().toLocaleDateString()}</p>

<h3>1. Information We Collect</h3>
<p>We collect information you provide directly to us, such as when you create an account, make a booking, or contact us for support.</p>

<h3>2. How We Use Your Information</h3>
<p>We use the information we collect to:</p>
<ul>
<li>Process your tour bookings</li>
<li>Send you booking confirmations and updates</li>
<li>Respond to your comments and questions</li>
<li>Improve our services</li>
</ul>

<h3>3. Information Sharing</h3>
<p>We do not sell your personal information. We may share your information with tour operators to fulfill your bookings.</p>

<h3>4. Contact Us</h3>
<p>If you have questions about this Privacy Policy, please contact us.</p>`
    },
    {
      name: 'Cancellation Policy',
      slug: 'cancellation-policy',
      content: `<h2>Cancellation Policy</h2>

<h3>Standard Cancellation Terms</h3>
<p>We understand plans can change. Here's our cancellation policy:</p>

<h3>Full Refund</h3>
<ul>
<li>Cancel more than 24 hours before your tour for a full refund</li>
</ul>

<h3>No Refund</h3>
<ul>
<li>Cancellations within 24 hours of the tour start time</li>
<li>No-shows</li>
</ul>

<h3>Weather-Related Cancellations</h3>
<p>If we cancel a tour due to weather conditions, you will receive a full refund or the option to reschedule.</p>

<h3>How to Cancel</h3>
<p>To cancel your booking, please contact us via email or phone. Include your booking confirmation number.</p>`
    },
    {
      name: 'Terms of Service',
      slug: 'terms-of-service',
      content: `<h2>Terms of Service</h2>
<p>Last updated: ${new Date().toLocaleDateString()}</p>

<h3>1. Acceptance of Terms</h3>
<p>By using our services, you agree to these terms. If you do not agree, please do not use our services.</p>

<h3>2. Booking Terms</h3>
<p>When you make a booking, you agree to:</p>
<ul>
<li>Provide accurate information</li>
<li>Pay the listed price</li>
<li>Follow tour safety guidelines</li>
<li>Treat tour guides and other guests with respect</li>
</ul>

<h3>3. Liability</h3>
<p>Tour activities involve inherent risks. By participating, you acknowledge these risks and agree to our liability waiver.</p>

<h3>4. Changes to Services</h3>
<p>We reserve the right to modify or cancel tours due to weather, safety concerns, or other circumstances beyond our control.</p>`
    },
    {
      name: 'FAQ',
      slug: 'faq',
      content: `<h2>Frequently Asked Questions</h2>

<h3>What should I bring on a tour?</h3>
<p>We recommend bringing comfortable shoes, sunscreen, water, and a camera. Specific items may vary by tour - check your booking confirmation for details.</p>

<h3>Are tours available in Spanish?</h3>
<p>Yes! Many of our tours are offered in both English and Spanish. Please let us know your language preference when booking.</p>

<h3>What happens if it rains?</h3>
<p>Light rain usually doesn't stop our tours. For severe weather, we'll contact you to reschedule or provide a full refund.</p>

<h3>Is transportation included?</h3>
<p>Transportation varies by tour. Check the tour details or contact us for specific information about your chosen tour.</p>

<h3>Can I book for a group?</h3>
<p>Yes! We welcome group bookings. Contact us for special group rates and private tour options.</p>`
    },
    {
      name: 'Contact Us',
      slug: 'contact',
      content: `<h2>Contact Us</h2>

<p>We'd love to hear from you! Get in touch with our team.</p>

<h3>Customer Service</h3>
<p>For booking questions, tour information, or general inquiries:</p>
<ul>
<li><strong>Email:</strong> hello@tourfinder.pr</li>
<li><strong>Phone:</strong> +1 (787) 555-0123</li>
<li><strong>WhatsApp:</strong> Available for quick responses</li>
</ul>

<h3>Office Hours</h3>
<p>Monday - Saturday: 8:00 AM - 6:00 PM<br/>
Sunday: 9:00 AM - 4:00 PM</p>

<h3>Office Location</h3>
<p>123 Calle del Sol<br/>
San Juan, PR 00901</p>

<h3>Social Media</h3>
<p>Follow us for tour updates and travel inspiration!</p>`
    },
    {
      name: 'Waiver Information',
      slug: 'waiver',
      content: `<h2>Waiver and Release Information</h2>

<p>For your safety and ours, all participants must sign a liability waiver before participating in tours.</p>

<h3>Why We Require Waivers</h3>
<p>Tour activities involve inherent risks. The waiver ensures you understand these risks and agree to participate responsibly.</p>

<h3>What the Waiver Covers</h3>
<ul>
<li>Assumption of risk for tour activities</li>
<li>Release of liability for accidents or injuries</li>
<li>Medical condition acknowledgment</li>
<li>Photo/video consent</li>
<li>Agreement to follow safety rules</li>
</ul>

<h3>When to Sign</h3>
<p>You'll be asked to sign the waiver during the booking process for each guest in your party.</p>

<h3>Questions?</h3>
<p>If you have questions about our waiver, please contact us before booking.</p>`
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="custom-pages-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold">Custom Pages</h1>
          <p className="text-muted-foreground">Create and manage pages like Privacy Policy, FAQ, Contact, etc.</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="gap-2" data-testid="create-page-btn">
          <Plus className="h-4 w-4" />
          Create Page
        </Button>
      </div>

      {/* Quick Templates */}
      {pages.length === 0 && (
        <Card className="p-6 mb-6 bg-blue-50/50 border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-2">Quick Start Templates</h3>
          <p className="text-sm text-blue-800 mb-4">
            Get started quickly with pre-built page templates. Click any template to create that page.
          </p>
          <div className="flex flex-wrap gap-2">
            {pageTemplates.map((template) => (
              <Button
                key={template.slug}
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingPage(null);
                  setFormData({
                    title: template.name,
                    slug: template.slug,
                    content: template.content,
                    meta_description: `${template.name} for Tour Finder Puerto Rico`,
                    is_active: true,
                  });
                  setShowDialog(true);
                }}
                className="bg-white"
              >
                + {template.name}
              </Button>
            ))}
          </div>
        </Card>
      )}

      {/* Pages List */}
      <Card className="p-6">
        {pages.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No pages created yet. Create your first page above.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pages.map((page) => (
              <div 
                key={page.id} 
                className="flex items-center justify-between p-4 bg-muted/30 rounded-lg"
                data-testid={`page-item-${page.id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold">{page.title}</h3>
                    {page.is_active ? (
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Draft</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    URL: <code className="bg-muted px-2 py-0.5 rounded">/page/{page.slug}</code>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/page/${page.slug}`, '_blank')}
                    className="gap-1"
                  >
                    <Eye className="h-4 w-4" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenDialog(page)}
                    className="gap-1"
                  >
                    <Edit2 className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(page.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Info Card */}
      <Card className="p-6 mt-6 bg-amber-50/50 border-amber-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-900">Linking Pages to Footer</h3>
            <p className="text-sm text-amber-800 mt-1">
              After creating pages, go to <strong>Edit Footer</strong> tab to add them to your footer's Support or Explore links. 
              Use the format <code className="bg-amber-100 px-1 rounded">/page/your-slug</code> as the URL.
            </p>
          </div>
        </div>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingPage ? 'Edit Page' : 'Create New Page'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Page Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => {
                    const newTitle = e.target.value;
                    setFormData({ 
                      ...formData, 
                      title: newTitle,
                      slug: !editingPage ? generateSlug(newTitle) : formData.slug
                    });
                  }}
                  placeholder="e.g., Privacy Policy"
                  required
                  data-testid="page-title-input"
                />
              </div>
              <div>
                <Label>URL Slug *</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">/page/</span>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: generateSlug(e.target.value) })}
                    placeholder="e.g., privacy-policy"
                    required
                    data-testid="page-slug-input"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label>Meta Description (for SEO)</Label>
              <Input
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                placeholder="Brief description for search engines..."
                data-testid="page-meta-input"
              />
            </div>

            <div>
              <Label>Page Content *</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Use the toolbar to format your content. Headings, bold, lists, and links are all supported.
              </p>
              <RichTextEditor
                value={formData.content}
                onChange={(content) => setFormData({ ...formData, content })}
                placeholder="Start writing your page content..."
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="page-active-toggle"
              />
              <Label>Page is Active (publicly visible)</Label>
            </div>

            {/* Quick Templates (when creating) */}
            {!editingPage && (
              <div>
                <Label className="mb-2 block">Quick Templates</Label>
                <div className="flex flex-wrap gap-2">
                  {pageTemplates.map((template) => (
                    <Button
                      key={template.slug}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({
                        title: template.name,
                        slug: template.slug,
                        content: template.content,
                        meta_description: `${template.name} for Tour Finder Puerto Rico`,
                        is_active: true,
                      })}
                    >
                      {template.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-page-btn">
                {editingPage ? 'Save Changes' : 'Create Page'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Customer Profiles Tab Component
function CustomerProfilesTab({ onRefresh }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [selectedEmailType, setSelectedEmailType] = useState('');
  const [siteSettings, setSiteSettings] = useState({});

  useEffect(() => {
    loadCustomers();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await adminAPI.getSettings();
      setSiteSettings(response.data || {});
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await adminAPI.getAllBookings();
      const bookingsData = response.data || [];
      
      // Group bookings by customer email to create customer profiles
      const customerMap = new Map();
      
      bookingsData.forEach(booking => {
        const email = booking.user_email || booking.customer_email;
        if (!email) return;
        
        if (customerMap.has(email)) {
          const customer = customerMap.get(email);
          customer.bookings.push(booking);
          customer.totalSpent += booking.total_price || 0;
          // Check waiver status
          if (booking.waiver_signed) customer.waiverSigned = true;
          // Check payment status
          if (booking.payment_status === 'paid') customer.hasPaidBooking = true;
        } else {
          customerMap.set(email, {
            email: email,
            name: booking.customer_name || booking.user_email?.split('@')[0] || 'Unknown',
            phone: booking.customer_phone || booking.phone || '',
            bookings: [booking],
            totalSpent: booking.total_price || 0,
            waiverSigned: booking.waiver_signed || false,
            hasPaidBooking: booking.payment_status === 'paid',
            firstBooking: booking.created_at,
          });
        }
      });
      
      // Convert to array and sort by most recent booking
      const customerList = Array.from(customerMap.values()).sort((a, b) => {
        const latestA = a.bookings.reduce((max, b) => b.created_at > max ? b.created_at : max, '');
        const latestB = b.bookings.reduce((max, b) => b.created_at > max ? b.created_at : max, '');
        return latestB.localeCompare(latestA);
      });
      
      setCustomers(customerList);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.phone?.includes(searchTerm)
  );

  const handleSendSMS = (phone) => {
    if (!phone) {
      toast.error('No phone number available for this customer');
      return;
    }
    window.open(`sms:${phone}`, '_blank');
  };

  const handleSendWhatsApp = (phone) => {
    if (!phone) {
      toast.error('No phone number available for this customer');
      return;
    }
    // Remove non-numeric characters and format for WhatsApp
    const cleanPhone = phone.replace(/\D/g, '');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  const handleSendEmail = async (customer, emailType) => {
    try {
      // Get the most recent booking for context
      const recentBooking = customer.bookings[0];
      
      await adminAPI.sendCustomerEmail({
        customer_email: customer.email,
        customer_name: customer.name,
        template_type: emailType,
        booking_id: recentBooking?.id || null
      });
      
      toast.success(`${emailType.replace(/_/g, ' ')} email queued for ${customer.email}`);
      setShowEmailDialog(false);
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    }
  };

  const getUpcomingBookings = (bookings) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bookings.filter(b => {
      const date = new Date(b.tour_date || b.date);
      return date >= today && b.status !== 'cancelled';
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="customer-profiles-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold">Customer Profiles</h1>
          <p className="text-muted-foreground">View and manage customer information, bookings, and communications</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-primary">{customers.length}</p>
          <p className="text-sm text-muted-foreground">Total Customers</p>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {filteredCustomers.length === 0 ? (
        <Card className="p-12 text-center">
          <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Customers Yet</h3>
          <p className="text-muted-foreground">Customer profiles will appear here when bookings are made</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredCustomers.map((customer, idx) => {
            const upcomingBookings = getUpcomingBookings(customer.bookings);
            const hasUpcoming = upcomingBookings.length > 0;
            
            return (
              <Card 
                key={idx} 
                className={`p-4 ${hasUpcoming ? 'border-l-4 border-l-primary' : ''}`}
                data-testid={`customer-${idx}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Customer Info Row */}
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-lg font-bold text-primary">
                          {customer.name?.charAt(0).toUpperCase() || '?'}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{customer.name}</h3>
                          {hasUpcoming && (
                            <Badge className="bg-primary text-white text-xs">
                              {upcomingBookings.length} Upcoming
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{customer.email}</p>
                        {customer.phone && (
                          <p className="text-sm text-muted-foreground">{customer.phone}</p>
                        )}
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="flex flex-wrap gap-4 mb-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{customer.bookings.length} booking(s)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{formatPrice(customer.totalSpent)} total</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {customer.waiverSigned ? (
                          <Badge className="bg-green-100 text-green-800 text-xs">
                            <CheckCircle className="h-3 w-3 mr-1" /> Waiver Signed
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 text-xs">
                            <AlertCircle className="h-3 w-3 mr-1" /> Waiver Pending
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {customer.hasPaidBooking ? (
                          <Badge className="bg-green-100 text-green-800 text-xs">
                            <CheckCircle className="h-3 w-3 mr-1" /> Has Paid
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800 text-xs">
                            <XCircle className="h-3 w-3 mr-1" /> Payment Pending
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Upcoming Bookings */}
                    {hasUpcoming && (
                      <div className="p-3 bg-primary/5 rounded-lg mb-3">
                        <p className="text-xs font-medium text-primary mb-2">Upcoming Tours:</p>
                        <div className="space-y-1">
                          {upcomingBookings.slice(0, 3).map((b, i) => (
                            <div key={i} className="text-sm flex items-center gap-2">
                              <span className="font-medium">{b.tour_title}</span>
                              <span className="text-muted-foreground">- {formatShortDate(b.tour_date || b.date)}</span>
                              {new Date(b.tour_date || b.date).toDateString() === new Date().toDateString() && (
                                <Badge className="bg-red-500 text-white text-xs">Today!</Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quick Actions */}
                  <div className="flex flex-col gap-2 ml-4">
                    {/* Contact Actions */}
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSendSMS(customer.phone)}
                        disabled={!customer.phone}
                        title="Send SMS"
                        className="h-9 px-3"
                      >
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSendWhatsApp(customer.phone)}
                        disabled={!customer.phone}
                        title="Send WhatsApp"
                        className="h-9 px-3 text-green-600 hover:text-green-700"
                      >
                        <Phone className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* Email Actions */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setShowEmailDialog(true);
                      }}
                      className="h-9"
                    >
                      <Mail className="h-4 w-4 mr-1" /> Send Email
                    </Button>
                    
                    {/* View Details */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedCustomer(selectedCustomer?.email === customer.email ? null : customer)}
                      className="h-9"
                    >
                      <Eye className="h-4 w-4 mr-1" /> 
                      {selectedCustomer?.email === customer.email ? 'Hide' : 'Details'}
                    </Button>
                  </div>
                </div>

                {/* Expanded Details */}
                {selectedCustomer?.email === customer.email && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-semibold mb-3">Booking History</h4>
                    <div className="space-y-2">
                      {customer.bookings.map((booking, bi) => (
                        <div key={bi} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <p className="font-medium">{booking.tour_title}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatShortDate(booking.tour_date || booking.date)} • {booking.guests} guest(s)
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{formatPrice(booking.total_price)}</span>
                            <Badge className={getStatusColor(booking.status)}>{booking.status}</Badge>
                            <Badge className={getPaymentStatusColor(booking.payment_status || 'pending')}>
                              {booking.payment_status || 'pending'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Send Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Email to {selectedCustomer?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Select an email template to send to {selectedCustomer?.email}
            </p>
            <div className="space-y-2">
              {[
                { type: 'booking_confirmation', label: 'Booking Confirmation', icon: CheckCircle },
                { type: 'tour_reminder', label: 'Tour Reminder (24hr)', icon: Bell },
                { type: 'waiver_reminder', label: 'Waiver Reminder', icon: FileText },
                { type: 'payment_reminder', label: 'Payment Reminder', icon: CreditCard },
                { type: 'thank_you', label: 'Thank You / Review Request', icon: Star },
              ].map(({ type, label, icon: Icon }) => (
                <Button
                  key={type}
                  variant="outline"
                  className="w-full justify-start h-auto py-3"
                  onClick={() => handleSendEmail(selectedCustomer, type)}
                >
                  <Icon className="h-4 w-4 mr-3 text-primary" />
                  <span>{label}</span>
                </Button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Pickup Hotels Tab Component
function PickupHotelsTab({ onRefresh }) {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    area: '',
    is_active: true,
  });

  const loadHotels = async () => {
    try {
      const response = await adminAPI.getPickupHotels();
      setHotels(response.data || []);
    } catch (error) {
      console.error('Error loading hotels:', error);
      toast.error('Failed to load hotels');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHotels();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingHotel) {
        await adminAPI.updatePickupHotel(editingHotel.id, formData);
        toast.success('Hotel updated successfully');
      } else {
        await adminAPI.createPickupHotel(formData);
        toast.success('Hotel added successfully');
      }
      
      setShowDialog(false);
      setEditingHotel(null);
      setFormData({ name: '', address: '', area: '', is_active: true });
      loadHotels();
    } catch (error) {
      console.error('Error saving hotel:', error);
      toast.error('Failed to save hotel');
    }
  };

  const handleDelete = async (hotelId) => {
    if (!window.confirm('Are you sure you want to delete this hotel?')) return;
    
    try {
      await adminAPI.deletePickupHotel(hotelId);
      toast.success('Hotel deleted successfully');
      loadHotels();
    } catch (error) {
      console.error('Error deleting hotel:', error);
      toast.error('Failed to delete hotel');
    }
  };

  const openEditDialog = (hotel) => {
    setEditingHotel(hotel);
    setFormData({
      name: hotel.name,
      address: hotel.address,
      area: hotel.area || '',
      is_active: hotel.is_active !== false,
    });
    setShowDialog(true);
  };

  const openAddDialog = () => {
    setEditingHotel(null);
    setFormData({ name: '', address: '', area: '', is_active: true });
    setShowDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="hotels-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold">Pickup Hotels</h1>
          <p className="text-muted-foreground">Manage hotel pickup locations for tour bookings</p>
        </div>
        <Button onClick={openAddDialog} className="gap-2" data-testid="add-hotel-btn">
          <Plus className="h-4 w-4" />
          Add Hotel
        </Button>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Hotel Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Address</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Area</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {hotels.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    No hotels added yet. Click "Add Hotel" to create one.
                  </td>
                </tr>
              ) : (
                hotels.map((hotel) => (
                  <tr key={hotel.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium">{hotel.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{hotel.address}</td>
                    <td className="px-4 py-3">
                      {hotel.area && (
                        <Badge className="bg-primary/10 text-primary">{hotel.area}</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={hotel.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {hotel.is_active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(hotel)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(hotel.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add/Edit Hotel Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingHotel ? 'Edit Hotel' : 'Add New Hotel'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Hotel Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Marriott San Juan Resort"
                required
              />
            </div>
            <div>
              <Label>Address *</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="e.g., 1309 Ashford Ave, San Juan, PR 00907"
                required
              />
            </div>
            <div>
              <Label>Area / Zone</Label>
              <Input
                value={formData.area}
                onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                placeholder="e.g., Condado, Old San Juan, Isla Verde"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <Label>Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingHotel ? 'Update Hotel' : 'Add Hotel'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Waiver Tab Component
function WaiverTab({ onRefresh }) {
  const [waiver, setWaiver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
  });

  const loadWaiver = async () => {
    try {
      const response = await adminAPI.getWaiver();
      const data = response.data;
      setWaiver(data);
      setFormData({
        title: data.title || 'Liability Waiver and Release Form',
        content: data.content || '',
      });
    } catch (error) {
      console.error('Error loading waiver:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWaiver();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await adminAPI.updateWaiver(formData);
      toast.success('Waiver updated successfully');
    } catch (error) {
      console.error('Error saving waiver:', error);
      toast.error('Failed to save waiver');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="waiver-tab">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold">Waiver Management</h1>
        <p className="text-muted-foreground">Manage waiver templates and send reminders to guests</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="p-6">
          <div className="space-y-4">
            <div>
              <Label className="text-lg font-semibold">Waiver Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Liability Waiver and Release Form"
                className="mt-2"
              />
            </div>

            <div>
              <Label className="text-lg font-semibold">Waiver Content (HTML)</Label>
              <p className="text-sm text-muted-foreground mb-2">
                Use HTML tags for formatting (h2, h3, p, ul, li, strong, etc.)
              </p>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Enter waiver content with HTML formatting..."
                className="w-full px-4 py-3 border rounded-lg font-mono text-sm resize-none"
                rows={15}
              />
            </div>
          </div>
        </Card>

        {/* Preview */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Preview</h2>
          <div className="border rounded-lg p-6 bg-white max-h-96 overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">{formData.title}</h3>
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formData.content) }}
            />
          </div>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save Waiver
          </Button>
        </div>
      </form>
    </div>
  );
}

// Waiver History Tab Component
function WaiverHistoryTab() {
  const [waivers, setWaivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
  });
  const [selectedWaiver, setSelectedWaiver] = useState(null);

  const loadWaivers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      
      const response = await adminAPI.getWaiverHistory(params);
      setWaivers(response.data?.waivers || []);
    } catch (error) {
      console.error('Error loading waivers:', error);
      toast.error('Failed to load waiver history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWaivers();
  }, []);

  // Filter waivers by name (client-side search)
  const filteredWaivers = waivers.filter(waiver => {
    if (!searchName.trim()) return true;
    const searchLower = searchName.toLowerCase();
    return (
      waiver.guest_name?.toLowerCase().includes(searchLower) ||
      waiver.booker_name?.toLowerCase().includes(searchLower) ||
      waiver.booker_email?.toLowerCase().includes(searchLower)
    );
  });

  // Generate PDF for a single waiver
  const generateWaiverPDF = async (waiver) => {
    setExporting(true);
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.setTextColor(22, 101, 52); // Green color
      doc.text('TOUR FINDER', 105, 20, { align: 'center' });
      
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text('Liability Waiver Certificate', 105, 30, { align: 'center' });
      
      doc.setDrawColor(22, 101, 52);
      doc.setLineWidth(0.5);
      doc.line(20, 35, 190, 35);
      
      // Waiver Details
      doc.setFontSize(12);
      let y = 50;
      
      doc.setFont(undefined, 'bold');
      doc.text('Guest Information', 20, y);
      doc.setFont(undefined, 'normal');
      y += 10;
      
      doc.text(`Name: ${waiver.guest_name}`, 20, y);
      y += 8;
      doc.text(`Date of Birth: ${waiver.guest_dob || 'N/A'}`, 20, y);
      y += 15;
      
      doc.setFont(undefined, 'bold');
      doc.text('Tour Information', 20, y);
      doc.setFont(undefined, 'normal');
      y += 10;
      
      doc.text(`Tour: ${waiver.tour_title}`, 20, y);
      y += 8;
      doc.text(`Date: ${waiver.tour_date} at ${waiver.tour_time}`, 20, y);
      y += 15;
      
      doc.setFont(undefined, 'bold');
      doc.text('Booking Information', 20, y);
      doc.setFont(undefined, 'normal');
      y += 10;
      
      doc.text(`Booked by: ${waiver.booker_name}`, 20, y);
      y += 8;
      doc.text(`Email: ${waiver.booker_email}`, 20, y);
      y += 8;
      doc.text(`Signed on: ${new Date(waiver.signed_at).toLocaleString()}`, 20, y);
      y += 20;
      
      // Signature section
      doc.setFont(undefined, 'bold');
      doc.text('Digital Signature', 20, y);
      doc.setFont(undefined, 'normal');
      y += 10;
      
      if (waiver.signature_data) {
        // Add signature image
        try {
          doc.addImage(waiver.signature_data, 'PNG', 20, y, 80, 40);
          y += 50;
        } catch (err) {
          doc.text('[Signature on file]', 20, y);
          y += 10;
        }
      } else {
        doc.text('[No signature image available]', 20, y);
        y += 10;
      }
      
      // Footer
      doc.setFontSize(10);
      doc.setTextColor(128, 128, 128);
      doc.text('This document certifies that the above guest has signed the liability waiver.', 105, 270, { align: 'center' });
      doc.text(`Generated on ${new Date().toLocaleString()}`, 105, 280, { align: 'center' });
      
      // Save
      const fileName = `waiver_${waiver.guest_name.replace(/\s+/g, '_')}_${waiver.tour_date}.pdf`;
      doc.save(fileName);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = { format };
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      
      if (format === 'csv') {
        // Download CSV
        const response = await adminAPI.exportWaivers({ ...params, format: 'csv' });
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `waivers_export_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        toast.success('Waivers exported successfully');
      } else {
        const response = await adminAPI.exportWaivers(params);
        const jsonStr = JSON.stringify(response.data.export_data, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `waivers_export_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        toast.success('Waivers exported successfully');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export waivers');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="waiver-history-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold">Waiver History</h1>
          <p className="text-muted-foreground mt-1">View and download signed waivers from bookings</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExport('json')} disabled={exporting} className="gap-2">
            <FileText className="h-4 w-4" />
            Export JSON
          </Button>
          <Button variant="outline" onClick={() => handleExport('csv')} disabled={exporting} className="gap-2">
            <FileText className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-sm">Search by Name</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search guest or booker name..."
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                className="pl-9"
                data-testid="waiver-search-name"
              />
            </div>
          </div>
          <div>
            <Label className="text-sm">From Date</Label>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="w-40"
            />
          </div>
          <div>
            <Label className="text-sm">To Date</Label>
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="w-40"
            />
          </div>
          <Button onClick={loadWaivers} variant="default" className="gap-2">
            <Search className="h-4 w-4" />
            Filter
          </Button>
          <Button 
            variant="ghost" 
            onClick={() => {
              setFilters({ dateFrom: '', dateTo: '' });
              setSearchName('');
              loadWaivers();
            }}
          >
            Clear
          </Button>
        </div>
      </Card>

      {/* Waivers List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filteredWaivers.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold mb-2">No Signed Waivers Found</h3>
          <p className="text-muted-foreground">
            {searchName ? 'No waivers match your search.' : 'Waivers will appear here after customers complete bookings.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{filteredWaivers.length} signed waiver{filteredWaivers.length !== 1 ? 's' : ''} found</p>
          
          <div className="grid gap-4">
            {filteredWaivers.map((waiver) => (
              <Card key={waiver.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="font-semibold">{waiver.guest_name}</span>
                      <span className="text-sm text-muted-foreground">
                        (DOB: {waiver.guest_dob})
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <MapIcon className="h-3 w-3" />
                        {waiver.tour_title}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {waiver.tour_date} at {waiver.tour_time}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Signed: {new Date(waiver.signed_at).toLocaleString()}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Booked by: {waiver.booker_name} ({waiver.booker_email})
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {waiver.signature_data && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedWaiver(waiver)}
                          className="gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          View
                        </Button>
                        <Button 
                          variant="default" 
                          size="sm"
                          onClick={() => generateWaiverPDF(waiver)}
                          disabled={exporting}
                          className="gap-1 bg-red-600 hover:bg-red-700"
                          data-testid={`download-pdf-${waiver.id}`}
                        >
                          <FileText className="h-3 w-3" />
                          PDF
                        </Button>
                      </>
                    )}
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                      waiver.payment_status === 'paid' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {waiver.payment_status === 'paid' ? (
                        <>
                          <CheckCircle className="h-3 w-3" />
                          Paid
                        </>
                      ) : (
                        <>
                          <Clock className="h-3 w-3" />
                          Pending
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                      <CheckCircle className="h-3 w-3" />
                      Signed
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Signature View Dialog */}
      <Dialog open={!!selectedWaiver} onOpenChange={() => setSelectedWaiver(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Waiver Signature</DialogTitle>
          </DialogHeader>
          {selectedWaiver && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">Guest Name</Label>
                  <p className="font-semibold">{selectedWaiver.guest_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Date of Birth</Label>
                  <p className="font-semibold">{selectedWaiver.guest_dob}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Tour</Label>
                  <p className="font-semibold">{selectedWaiver.tour_title}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Signed At</Label>
                  <p className="font-semibold">{new Date(selectedWaiver.signed_at).toLocaleString()}</p>
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground mb-2 block">Signature</Label>
                <div className="border rounded-lg p-4 bg-white">
                  <img 
                    src={selectedWaiver.signature_data} 
                    alt="Signature" 
                    className="max-w-full h-auto"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button 
                  onClick={() => generateWaiverPDF(selectedWaiver)}
                  disabled={exporting}
                  className="gap-2 bg-red-600 hover:bg-red-700"
                >
                  <FileText className="h-4 w-4" />
                  Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Reminders Tab Component
function RemindersTab() {
  const [pendingReminders, setPendingReminders] = useState([]);
  const [reminderLogs, setReminderLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pendingRes, logsRes] = await Promise.all([
        adminAPI.getPendingReminders(),
        adminAPI.getReminderLogs(50),
      ]);
      setPendingReminders(pendingRes.data?.bookings || []);
      setReminderLogs(logsRes.data?.logs || []);
    } catch (error) {
      console.error('Error loading reminders:', error);
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSendReminders = async () => {
    setSending(true);
    try {
      const response = await adminAPI.sendUpcomingReminders();
      toast.success(response.data?.message || 'Reminders sent successfully');
      loadData();
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast.error('Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="reminders-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold">Tour Reminders</h1>
          <p className="text-muted-foreground mt-1">Send 24-hour reminders to customers</p>
        </div>
        <Button 
          onClick={handleSendReminders} 
          disabled={sending || pendingReminders.length === 0}
          className="gap-2"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send Reminders ({pendingReminders.length})
        </Button>
      </div>

      {/* Pending Reminders */}
      <Card className="p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Bell className="h-5 w-5 text-amber-500" />
          Pending Reminders (Tours in next 24-48 hours)
        </h2>
        
        {pendingReminders.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
            <p>All reminders sent! No pending reminders.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingReminders.map((booking) => (
              <div 
                key={booking.id} 
                className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg"
              >
                <div className="space-y-1">
                  <div className="font-semibold">{booking.tour_title}</div>
                  <div className="text-sm text-muted-foreground">
                    {booking.booker_name} • {booking.booker_email}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {booking.date} at {booking.time} • {booking.guests} guests
                  </div>
                </div>
                <div className="text-amber-600 text-sm font-medium">
                  Reminder Pending
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Reminder Logs */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Recent Reminder Logs
        </h2>
        
        {reminderLogs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No reminder logs yet.</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {reminderLogs.map((log) => (
              <div 
                key={log.id} 
                className="flex items-center justify-between p-3 border rounded-lg text-sm"
              >
                <div className="space-y-1">
                  <div className="font-medium">{log.tour_title}</div>
                  <div className="text-muted-foreground">
                    To: {log.recipient_email}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Tour: {log.tour_date} at {log.tour_time}
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-xs px-2 py-1 rounded-full ${
                    log.status === 'sent_mocked' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {log.status === 'sent_mocked' ? 'Sent (Mocked)' : log.status}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {new Date(log.sent_at).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
          <strong>Note:</strong> Email and SMS reminders are currently in mock mode. 
          Connect a real email provider to send actual notifications.
        </div>
      </Card>
    </div>
  );
}

// Featured Section Tab Component
function FeaturedSectionTab({ tours, onRefresh }) {
  const [saving, setSaving] = useState(false);
  const [selectedTours, setSelectedTours] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Load currently featured tours
  useEffect(() => {
    const featuredTours = tours.filter(t => t.is_featured);
    setSelectedTours(featuredTours.slice(0, 2)); // Max 2 featured
  }, [tours]);

  const handleToggleFeatured = async (tour) => {
    const isCurrentlySelected = selectedTours.some(t => t.id === tour.id);
    
    if (isCurrentlySelected) {
      // Remove from featured
      setSelectedTours(selectedTours.filter(t => t.id !== tour.id));
      try {
        await adminAPI.updateTour(tour.id, { is_featured: false });
        toast.success(`${tour.title} removed from featured`);
        onRefresh();
      } catch (error) {
        toast.error('Failed to update tour');
      }
    } else {
      // Add to featured (max 2)
      if (selectedTours.length >= 2) {
        toast.error('Maximum 2 featured tours allowed. Remove one first.');
        return;
      }
      setSelectedTours([...selectedTours, tour]);
      try {
        await adminAPI.updateTour(tour.id, { is_featured: true });
        toast.success(`${tour.title} added to featured`);
        onRefresh();
      } catch (error) {
        toast.error('Failed to update tour');
      }
    }
  };

  const handleUpdateBadge = async (tourId, badge, color) => {
    try {
      await adminAPI.updateTour(tourId, { 
        featured_badge: badge,
        featured_badge_color: color
      });
      toast.success('Badge updated');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update badge');
    }
  };

  const filteredTours = tours.filter(t => 
    t.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="featured-section-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold">Featured Section</h1>
          <p className="text-muted-foreground mt-1">Select up to 2 tours to feature on homepage</p>
        </div>
      </div>

      {/* Currently Featured Tours */}
      <Card className="p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
          Currently Featured ({selectedTours.length}/2)
        </h2>
        
        {selectedTours.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Star className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No tours featured yet. Select tours below to feature them.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {selectedTours.map((tour) => (
              <div 
                key={tour.id} 
                className="relative border-2 border-amber-300 rounded-xl overflow-hidden bg-amber-50"
              >
                <div className="flex gap-4 p-4">
                  <img 
                    src={tour.image_url} 
                    alt={tour.title}
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold">{tour.title}</h3>
                    <p className="text-sm text-muted-foreground">${tour.price}</p>
                    
                    {/* Badge Customization */}
                    <div className="mt-2 space-y-2">
                      <div>
                        <Label className="text-xs">Badge Text</Label>
                        <Input
                          value={tour.featured_badge || 'Featured'}
                          onChange={(e) => handleUpdateBadge(tour.id, e.target.value, tour.featured_badge_color)}
                          className="h-8 text-sm"
                          placeholder="Badge text"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Badge Color</Label>
                        <div className="flex gap-2 mt-1">
                          {[
                            { value: 'white', bg: 'bg-white border-gray-300', ring: 'ring-gray-400' },
                            { value: 'red', bg: 'bg-red-500', ring: 'ring-red-500' },
                            { value: 'green', bg: 'bg-green-500', ring: 'ring-green-500' },
                            { value: 'yellow', bg: 'bg-yellow-400', ring: 'ring-yellow-400' },
                          ].map((color) => (
                            <button
                              key={color.value}
                              type="button"
                              onClick={() => handleUpdateBadge(tour.id, tour.featured_badge, color.value)}
                              className={`w-6 h-6 rounded-full border ${color.bg} ${
                                tour.featured_badge_color === color.value ? `ring-2 ${color.ring} ring-offset-1` : ''
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleToggleFeatured(tour)}
                    className="absolute top-2 right-2"
                  >
                    <XCircle className="h-5 w-5 text-red-500" />
                  </Button>
                </div>
                
                {/* Preview Badge */}
                <div className="px-4 pb-4">
                  <span className="text-xs text-muted-foreground">Preview: </span>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${
                    tour.featured_badge_color === 'white' ? 'bg-white text-gray-800 border' :
                    tour.featured_badge_color === 'red' ? 'bg-red-500 text-white' :
                    tour.featured_badge_color === 'yellow' ? 'bg-yellow-400 text-gray-900' :
                    'bg-green-500 text-white'
                  }`}>
                    {tour.featured_badge || 'Featured'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* All Tours Selection */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Select Tours to Feature</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tours..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
          {filteredTours.map((tour) => {
            const isSelected = selectedTours.some(t => t.id === tour.id);
            return (
              <div 
                key={tour.id}
                onClick={() => handleToggleFeatured(tour)}
                className={`flex gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  isSelected 
                    ? 'border-amber-400 bg-amber-50' 
                    : 'border-transparent hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <img 
                  src={tour.image_url} 
                  alt={tour.title}
                  className="w-16 h-16 object-cover rounded-lg"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm truncate">{tour.title}</h3>
                  <p className="text-sm text-muted-foreground">${tour.price}</p>
                  <p className="text-xs text-muted-foreground">{tour.category}</p>
                </div>
                {isSelected ? (
                  <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}